import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../../components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { ArrowUpCircle, Plus, Pencil, Trash2, Search, Upload, FileText, Check, ChevronsUpDown, X, FileCheck, Download, Filter, ArrowUpDown, ArrowUp, ArrowDown, Eye, Loader2, CreditCard, Briefcase, ChevronLeft, ChevronRight, Calendar, Wallet } from 'lucide-react';
import { cn } from '../../lib/utils';
import DocumentPreviewModal from '../../components/DocumentPreviewModal';
import JobDetailPopup from '../../components/JobDetailPopup';
import { useAuth } from '../../context/AuthContext';

// Timezone-safe date formatting (local date to YYYY-MM-DD)
const formatDateLocal = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const AccountingExpenses = ({ isBoardView = false }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [cikislar, setCikislar] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingCikis, setEditingCikis] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showDocPreview, setShowDocPreview] = useState(false);
  const [previewBelgeler, setPreviewBelgeler] = useState([]);
  
  // İş referansı state
  const [pendingJobAction, setPendingJobAction] = useState(null);
  
  // Job detail popup state
  const [showJobPopup, setShowJobPopup] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState(null);
  
  // Detail modal state
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailRecord, setDetailRecord] = useState(null);
  
  // Fatura ekleme state
  const [showFaturaModal, setShowFaturaModal] = useState(false);
  const [faturaForm, setFaturaForm] = useState({
    fatura_miktari: '',
    kdv_tutari: '',
    fatura_belgesi: null,
    fatura_tarihi: ''
  });
  const [faturaUploading, setFaturaUploading] = useState(false);
  
  // Board View - Approval/Review Modal States
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [approvalRecord, setApprovalRecord] = useState(null);
  const [approvalForm, setApprovalForm] = useState({
    status: '',
    approved_by: '',
    note: '',
    belgeler: []
  });
  const [approvalSubmitting, setApprovalSubmitting] = useState(false);
  
  // Sorting state
  const [sortConfig, setSortConfig] = useState({ key: 'tarih', direction: 'desc' });
  
  // Month navigation state
  const [currentMonth, setCurrentMonth] = useState(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() };
  });
  
  // Selected day state (null = tüm ay)
  const [selectedDay, setSelectedDay] = useState(null);
  
  // Month navigation functions
  const goToPreviousMonth = () => {
    setSelectedDay(null); // Ay değiştiğinde gün seçimini sıfırla
    setCurrentMonth(prev => {
      if (prev.month === 0) {
        return { year: prev.year - 1, month: 11 };
      }
      return { year: prev.year, month: prev.month - 1 };
    });
  };
  
  const goToNextMonth = () => {
    setSelectedDay(null); // Ay değiştiğinde gün seçimini sıfırla
    setCurrentMonth(prev => {
      if (prev.month === 11) {
        return { year: prev.year + 1, month: 0 };
      }
      return { year: prev.year, month: prev.month + 1 };
    });
  };
  
  const goToCurrentMonth = () => {
    const now = new Date();
    setCurrentMonth({ year: now.getFullYear(), month: now.getMonth() });
    setSelectedDay(null);
  };
  
  // Get days in month
  const getDaysInMonth = useMemo(() => {
    const daysInMonth = new Date(currentMonth.year, currentMonth.month + 1, 0).getDate();
    const firstDayOfMonth = new Date(currentMonth.year, currentMonth.month, 1).getDay();
    // Pazartesi = 0 olacak şekilde ayarla (JS'de Pazar = 0)
    const adjustedFirstDay = firstDayOfMonth === 0 ? 6 : firstDayOfMonth - 1;
    return { daysInMonth, firstDay: adjustedFirstDay };
  }, [currentMonth]);
  
  const dayNames = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'];
  
  // Select a specific day
  const selectDay = (day) => {
    if (selectedDay === day) {
      setSelectedDay(null); // Aynı güne tıklayınca seçimi kaldır
    } else {
      setSelectedDay(day);
    }
  };
  
  const monthNames = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];
  
  // Calculate month/day date range
  const getMonthDateRange = useMemo(() => {
    if (selectedDay) {
      // Belirli bir gün seçili
      const dayStr = String(selectedDay).padStart(2, '0');
      const monthStr = String(currentMonth.month + 1).padStart(2, '0');
      const dateStr = `${currentMonth.year}-${monthStr}-${dayStr}`;
      return {
        start: dateStr,
        end: dateStr
      };
    }
    // Tüm ay
    const startDate = new Date(currentMonth.year, currentMonth.month, 1);
    const endDate = new Date(currentMonth.year, currentMonth.month + 1, 0);
    return {
      start: startDate.toISOString().split('T')[0],
      end: endDate.toISOString().split('T')[0]
    };
  }, [currentMonth, selectedDay]);
  
  // Filter states
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [filterCariHesap, setFilterCariHesap] = useState('');
  const [filterHesap, setFilterHesap] = useState('');
  const [filterSube, setFilterSube] = useState('');
  const [filterEtiket, setFilterEtiket] = useState('');
  const [filterCekKarsiligi, setFilterCekKarsiligi] = useState('all');
  
  // Dropdown data
  const [cariHesaplar, setCariHesaplar] = useState([]);
  const [krediKartlari, setKrediKartlari] = useState([]);
  const [bankaHesaplari, setBankaHesaplari] = useState([]);
  const [nakitKasalar, setNakitKasalar] = useState([]);
  const [hesapKartlari, setHesapKartlari] = useState([]);
  const [branches, setBranches] = useState([]);
  const [odenecekCekler, setOdenecekCekler] = useState([]);
  const [devamEdenBorclar, setDevamEdenBorclar] = useState([]);
  
  // Combobox states
  const [cariOpen, setCariOpen] = useState(false);
  const [hesapOpen, setHesapOpen] = useState(false);
  const [etiketOpen, setEtiketOpen] = useState(false);
  const [cekOpen, setCekOpen] = useState(false);
  const [borcOdemeOpen, setBorcOdemeOpen] = useState(false);
  
  // Form state - tarih ve saat otomatik doldurulur
  const getDefaultDateTime = () => {
    const now = new Date();
    return {
      tarih: formatDateLocal(now),
      saat: now.toTimeString().slice(0, 5)
    };
  };
  
  const [form, setForm] = useState({
    ...getDefaultDateTime(),
    cari_hesap_id: '',
    cari_hesap_adi: '',
    miktar: '',
    para_birimi: 'TL',  // TL, USD, EUR
    hesap_id: '',
    hesap_adi: '',
    hesap_tipi: '',
    fortis_sube: '',
    etiket_id: '',
    etiket_adi: '',
    aciklama: '',
    belgeler: [],  // Çoklu belge desteği,
    cek_karsiligi: false,
    cek_id: '',
    cek_no: '',
    takipteki_borc: false,
    borc_takip_id: '',
    borc_odeme_id: '',
    borc_odeme_text: '',
    fatura_durumu: '',  // fatura_alindi, borc_verildi, fatura_kesilecek, borc_odendi
    fatura_miktari: '',  // Fatura alındı ise fatura miktarı
    kdv_orani: '20',     // KDV Oranı (%)
    kdv_tutari: '',  // KDV tutarı
    anlaşılan_fatura_miktari: '',  // Anlaşılan toplam fatura miktarı
    taksit_sayisi: 1,  // Kredi kartı taksit sayısı
    
    // Borç Takip Oluşturma
    create_takip: false,
    takip_toplam_tutar: '',
    takip_fatura_miktari: '',
    takip_kdv_detaylari: [{ id: Date.now(), miktar: '', kdv_orani: '20', kdv_tutari: '' }],
    takip_odemeler: [{ id: Date.now(), tarih: '', tutar: '', odeme_sekli: 'havale_eft', aciklama: '' }]
  });

  const fetchCikislar = useCallback(async () => {
    try {
      const token = localStorage.getItem('fortis_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/accounting/cikislar`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCikislar(data);
      }
    } catch (error) {
      console.error('Çıkışlar alınamadı:', error);
    }
  }, []);

  const fetchDropdownData = useCallback(async () => {
    try {
      const token = localStorage.getItem('fortis_token');
      const headers = { 'Authorization': `Bearer ${token}` };

      const [cariRes, krediRes, bankaRes, nakitKasaRes, etiketRes, branchRes, cekRes, borcRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/accounting/cari-hesaplar`, { headers }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/accounting/kredi-kartlari`, { headers }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/accounting/banka-hesaplari`, { headers }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/accounting/nakit-kasalar`, { headers }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/accounting/hesap-kartlari`, { headers }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/branches`, { headers }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/accounting/cekler/odenecek-verilen`, { headers }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/accounting/takipler/borc/devam-eden-odemeler`, { headers })
      ]);

      if (cariRes.ok) setCariHesaplar(await cariRes.json());
      if (krediRes.ok) setKrediKartlari(await krediRes.json());
      if (bankaRes.ok) setBankaHesaplari(await bankaRes.json());
      if (nakitKasaRes.ok) setNakitKasalar(await nakitKasaRes.json());
      if (etiketRes.ok) setHesapKartlari(await etiketRes.json());
      if (branchRes.ok) setBranches(await branchRes.json());
      if (cekRes.ok) setOdenecekCekler(await cekRes.json());
      if (borcRes.ok) setDevamEdenBorclar(await borcRes.json());
    } catch (error) {
      console.error('Dropdown verileri alınamadı:', error);
    }
  }, []);

  useEffect(() => {
    fetchCikislar();
    fetchDropdownData();
  }, [fetchCikislar, fetchDropdownData]);

  // İş referansı ile gelen işlemi kontrol et
  useEffect(() => {
    const pendingAction = localStorage.getItem('pending_job_action');
    if (pendingAction) {
      try {
        const action = JSON.parse(pendingAction);
        if (action.action_type === 'cikis') {
          setPendingJobAction(action);
          // Formu aç
          setShowModal(true);
          // localStorage'dan sil
          localStorage.removeItem('pending_job_action');
          toast.info(`"${action.job_konu}" işi için çıkış ekleniyor`);
        }
      } catch (e) {
        console.error('Job action parse error:', e);
      }
    }
  }, []);

  // Sorting function
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return <ArrowUpDown className="h-4 w-4 ml-1 opacity-50" />;
    }
    return sortConfig.direction === 'asc' 
      ? <ArrowUp className="h-4 w-4 ml-1" />
      : <ArrowDown className="h-4 w-4 ml-1" />;
  };

  // Board View - Approval Functions
  const openApprovalModal = (record) => {
    setApprovalRecord(record);
    setApprovalForm({
      status: record.approval_status || '',
      approved_by: record.approval_by || user?.name || '',
      note: record.approval_note || '',
      belgeler: record.approval_belgeler || []
    });
    setShowApprovalModal(true);
  };

  const handleApprovalSubmit = async () => {
    if (!approvalForm.status) {
      toast.error('Lütfen inceleme sonucu seçin');
      return;
    }
    if (!approvalForm.approved_by.trim()) {
      toast.error('İnceleyen adı gerekli');
      return;
    }

    setApprovalSubmitting(true);
    try {
      const response = await fetch(`${API}/api/accounting/approval/cikislar/${approvalRecord.id}`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(approvalForm)
      });

      if (response.ok) {
        toast.success('Onay durumu güncellendi');
        setShowApprovalModal(false);
        // Update local state
        setCikislar(prev => prev.map(c => 
          c.id === approvalRecord.id 
            ? { ...c, approval_status: approvalForm.status, approval_by: approvalForm.approved_by, approval_note: approvalForm.note, approval_belgeler: approvalForm.belgeler }
            : c
        ));
      } else {
        toast.error('Onay durumu güncellenemedi');
      }
    } catch (error) {
      console.error('Approval error:', error);
      toast.error('Onay işlemi sırasında hata oluştu');
    }
    setApprovalSubmitting(false);
  };

  const handleApprovalFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    const newBelgeler = [];
    
    for (const file of files) {
      const reader = new FileReader();
      const base64 = await new Promise((resolve) => {
        reader.onload = () => resolve(reader.result);
        reader.readAsDataURL(file);
      });
      
      newBelgeler.push({
        name: file.name,
        size: file.size,
        type: file.type,
        url: base64
      });
    }
    
    setApprovalForm(prev => ({ ...prev, belgeler: [...prev.belgeler, ...newBelgeler] }));
  };

  const getApprovalIcon = (status) => {
    switch(status) {
      case 'dogru':
        return <Check className="h-4 w-4 text-green-600" />;
      case 'yanlis':
        return <X className="h-4 w-4 text-red-600" />;
      case 'supheli':
        return <div className="h-4 w-4 rounded-full border-2 border-amber-500" />;
      default:
        return <Plus className="h-4 w-4 text-gray-400" />;
    }
  };

  // Filter and sort data
  const filteredAndSortedCikislar = useMemo(() => {
    let result = [...cikislar];

    // Apply month filter by default
    const monthRange = getMonthDateRange;
    result = result.filter(c => {
      if (!c.tarih) return false;
      return c.tarih >= monthRange.start && c.tarih <= monthRange.end;
    });

    // Apply text search
    if (searchTerm) {
      result = result.filter(c =>
        c.cari_hesap_adi?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.aciklama?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.etiket_adi?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.fortis_sube?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.cek_no?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply additional date filters (within selected month)
    if (filterDateFrom) {
      result = result.filter(c => c.tarih >= filterDateFrom);
    }
    if (filterDateTo) {
      result = result.filter(c => c.tarih <= filterDateTo);
    }

    // Apply cari hesap filter
    if (filterCariHesap) {
      result = result.filter(c => c.cari_hesap_adi?.toLowerCase().includes(filterCariHesap.toLowerCase()));
    }

    // Apply hesap filter
    if (filterHesap && filterHesap !== 'all_hesaplar') {
      result = result.filter(c => c.hesap_adi === filterHesap);
    }

    // Apply şube filter
    if (filterSube && filterSube !== 'all_branches') {
      result = result.filter(c => c.fortis_sube === filterSube);
    }

    // Apply etiket filter
    if (filterEtiket && filterEtiket !== 'all_tags') {
      result = result.filter(c => c.etiket_adi === filterEtiket);
    }

    // Apply çek karşılığı filter
    if (filterCekKarsiligi === 'yes') {
      result = result.filter(c => c.cek_karsiligi === true);
    } else if (filterCekKarsiligi === 'no') {
      result = result.filter(c => !c.cek_karsiligi);
    }

    // Apply sorting
    result.sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      if (sortConfig.key === 'miktar') {
        aValue = parseFloat(aValue) || 0;
        bValue = parseFloat(bValue) || 0;
      }

      if (aValue == null) aValue = '';
      if (bValue == null) bValue = '';

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });

    return result;
  }, [cikislar, searchTerm, filterDateFrom, filterDateTo, filterCariHesap, filterHesap, filterSube, filterEtiket, filterCekKarsiligi, sortConfig, getMonthDateRange]);

  // Export to Excel
  const exportToExcel = () => {
    if (filteredAndSortedCikislar.length === 0) {
      toast.error('İndirilecek veri bulunamadı');
      return;
    }

    const headers = ['Tarih', 'Saat', 'Cari Hesap', 'Miktar', 'Para Birimi', 'Hesap', 'Hesap Tipi', 'Çek No', 'Çek Karşılığı', 'Şube', 'Etiket', 'Açıklama'];
    const rows = filteredAndSortedCikislar.map(c => [
      c.tarih || '',
      c.saat || '',
      c.cari_hesap_adi || '',
      c.miktar || '0',
      c.para_birimi || 'TL',
      c.hesap_adi || '',
      c.hesap_tipi === 'kredi_karti' ? 'Kredi Kartı' : c.hesap_tipi === 'banka_hesabi' ? 'Banka Hesabı' : c.hesap_tipi === 'nakit_kasa' ? 'Nakit Kasa' : '',
      c.cek_no || '',
      c.cek_karsiligi ? 'Evet' : 'Hayır',
      c.fortis_sube || '',
      c.etiket_adi || '',
      c.aciklama || ''
    ]);

    const BOM = '\uFEFF';
    const csvContent = BOM + [headers, ...rows].map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(';')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `cikislar_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast.success('Excel dosyası indirildi');
  };

  const clearFilters = () => {
    setFilterDateFrom('');
    setFilterDateTo('');
    setFilterCariHesap('');
    setFilterHesap('');
    setFilterSube('');
    setFilterEtiket('');
    setFilterCekKarsiligi('all');
    setSearchTerm('');
  };

  const openNewModal = () => {
    const now = new Date();
    setEditingCikis(null);
    setForm({
      tarih: now.toISOString().split('T')[0],
      saat: now.toTimeString().slice(0, 5),
      cari_hesap_id: '',
      cari_hesap_adi: '',
      miktar: '',
      para_birimi: 'TL',
      hesap_id: '',
      hesap_adi: '',
      hesap_tipi: '',
      fortis_sube: '',
      etiket_id: '',
      etiket_adi: '',
      aciklama: '',
      belgeler: [],  // Çoklu belge desteği,
      cek_karsiligi: false,
      cek_id: '',
      cek_no: '',
      takipteki_borc: false,
      borc_takip_id: '',
      borc_odeme_id: '',
      borc_odeme_text: '',
      fatura_durumu: '',
      fatura_miktari: '',
      kdv_orani: '20',
      kdv_tutari: '',
      anlaşılan_fatura_miktari: '',
      suan_kesilen_miktar: '',
      suan_kesilen_fatura_miktari: '',
      suan_kesilen_kdv_orani: '20',
      suan_kesilen_kdv_tutari: '',
      taksit_sayisi: 1,
      create_takip: false,
      takip_toplam_tutar: '',
      takip_fatura_miktari: '',
      takip_kdv_detaylari: [{ id: Date.now(), miktar: '', kdv_orani: '20', kdv_tutari: '' }],
      takip_odemeler: [{ id: Date.now(), tarih: '', tutar: '', odeme_sekli: 'havale_eft', aciklama: '' }]
    });
    setShowModal(true);
  };

  const openEditModal = (cikis) => {
    setEditingCikis(cikis);
    setForm({
      tarih: cikis.tarih || '',
      saat: cikis.saat || '',
      cari_hesap_id: cikis.cari_hesap_id || '',
      cari_hesap_adi: cikis.cari_hesap_adi || '',
      miktar: cikis.miktar || '',
      para_birimi: cikis.para_birimi || 'TL',
      hesap_id: cikis.hesap_id || '',
      hesap_adi: cikis.hesap_adi || '',
      hesap_tipi: cikis.hesap_tipi || '',
      fortis_sube: cikis.fortis_sube || '',
      etiket_id: cikis.etiket_id || '',
      etiket_adi: cikis.etiket_adi || '',
      aciklama: cikis.aciklama || '',
      belgeler: cikis.belgeler || [],  // Çoklu belge desteği
      cek_karsiligi: cikis.cek_karsiligi || false,
      cek_id: cikis.cek_id || '',
      cek_no: cikis.cek_no || '',
      takipteki_borc: cikis.takipteki_borc || false,
      borc_takip_id: cikis.borc_takip_id || '',
      borc_odeme_id: cikis.borc_odeme_id || '',
      borc_odeme_text: cikis.borc_odeme_text || '',
      fatura_durumu: cikis.fatura_durumu || '',
      fatura_miktari: cikis.fatura_miktari || '',
      kdv_orani: cikis.kdv_orani || '20',
      kdv_tutari: cikis.kdv_tutari || '',
      anlaşılan_fatura_miktari: cikis.anlaşılan_fatura_miktari || '',
      suan_kesilen_miktar: '',
      suan_kesilen_fatura_miktari: '',
      suan_kesilen_kdv_orani: '20',
      suan_kesilen_kdv_tutari: '',
      taksit_sayisi: cikis.taksit_sayisi || 1,
      create_takip: false,
      takip_toplam_tutar: '',
      takip_fatura_miktari: '',
      takip_kdv_detaylari: [{ id: Date.now(), miktar: '', kdv_orani: '20', kdv_tutari: '' }],
      takip_odemeler: [{ id: Date.now(), tarih: '', tutar: '', odeme_sekli: 'havale_eft', aciklama: '' }]
    });
    setShowModal(true);
  };

  const handleSubmit = async () => {
    if (!form.miktar) {
      toast.error('Miktar gereklidir');
      return;
    }

    if (form.cek_karsiligi && !form.cek_id) {
      toast.error('Çek karşılığı seçildi, lütfen bir çek seçin');
      return;
    }

    if (form.takipteki_borc && !form.borc_odeme_id) {
      toast.error('Takipteki borç seçildi, lütfen bir ödeme seçin');
      return;
    }

    // KDV validasyonu - KDV tutarı fatura tutarından büyük olamaz
    if (form.fatura_miktari && form.kdv_tutari) {
      const faturaMiktar = parseFloat(form.fatura_miktari) || 0;
      const kdvTutar = parseFloat(form.kdv_tutari) || 0;
      if (kdvTutar > faturaMiktar) {
        toast.error('KDV tutarı fatura tutarından büyük olamaz');
        return;
      }
    }

    try {
      const token = localStorage.getItem('fortis_token');
      
      // Kredi kartı taksitli ödeme kontrolü
      const isKrediKartiTaksit = form.hesap_tipi === 'kredi_karti' && form.taksit_sayisi > 1 && !editingCikis;
      
      if (isKrediKartiTaksit) {
        // Taksitli ödeme - her ay için ayrı kayıt oluştur
        const toplamMiktar = parseFloat(form.miktar);
        const taksitMiktar = Math.round((toplamMiktar / form.taksit_sayisi) * 100) / 100;
        const startDate = new Date(form.tarih);
        
        let successCount = 0;
        for (let i = 0; i < form.taksit_sayisi; i++) {
          const taksitTarih = new Date(startDate);
          taksitTarih.setMonth(taksitTarih.getMonth() + i);
          
          const taksitForm = {
            ...form,
            tarih: formatDateLocal(taksitTarih),
            miktar: taksitMiktar,
            aciklama: `${form.aciklama || ''} (Taksit ${i + 1}/${form.taksit_sayisi})`.trim(),
            taksit_no: i + 1,
            toplam_taksit: form.taksit_sayisi,
            ana_tutar: toplamMiktar,
            // Şube bilgisini ekle (kasa kayıtları için)
            sube_id: user?.branch_id || null,
            sube: user?.branch_name || ''
          };
          
          const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/accounting/cikislar`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(taksitForm)
          });
          
          if (response.ok) successCount++;
        }
        
        if (successCount === form.taksit_sayisi) {
          toast.success(`${form.taksit_sayisi} taksit kaydı oluşturuldu`);
        } else {
          toast.warning(`${successCount}/${form.taksit_sayisi} taksit kaydı oluşturuldu`);
        }
        
        setShowModal(false);
        fetchCikislar();
        fetchDropdownData();
        return;
      }
      
      // Normal tek kayıt
      const url = editingCikis
        ? `${process.env.REACT_APP_BACKEND_URL}/api/accounting/cikislar/${editingCikis.id}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/accounting/cikislar`;

      // Fatura alındı/eksik fatura/fiş ise alinan_faturalar dizisine otomatik ekle
      let formData = { ...form };
      
      // Şube bilgisini ekle (kasa kayıtları için)
      if (user?.branch_id) {
        formData.sube_id = user.branch_id;
        formData.sube = user.branch_name || '';
      }
      
      // İş referansı ekle
      if (pendingJobAction && !editingCikis) {
        formData.job_id = pendingJobAction.job_id;
        formData.job_konu = pendingJobAction.job_konu;
      }
      
      const faturaDurumlari = ['fatura_alindi', 'eksik_fatura', 'fis'];
      if (!editingCikis && faturaDurumlari.includes(form.fatura_durumu)) {
        const faturaBelgesi = form.belgeler && form.belgeler.length > 0 ? form.belgeler[0] : null;
        let faturaMiktar = 0;
        let kdvTutar = 0;
        
        if (form.fatura_durumu === 'eksik_fatura' && form.suan_kesilen_fatura_miktari) {
          faturaMiktar = parseFloat(form.suan_kesilen_fatura_miktari) || 0;
          kdvTutar = parseFloat(form.suan_kesilen_kdv_tutari) || 0;
        } else if (form.fatura_miktari) {
          faturaMiktar = parseFloat(form.fatura_miktari) || 0;
          kdvTutar = parseFloat(form.kdv_tutari) || 0;
        }

        if (faturaMiktar > 0) {
          formData.alinan_faturalar = [{
            id: Date.now().toString(),
            miktar: faturaMiktar,
            kdv_tutari: kdvTutar,
            tarih: form.tarih,
            belge: faturaBelgesi,
            ekleme_tarihi: new Date().toISOString()
          }];
          formData.alinan_fatura_toplam = faturaMiktar;
        } else {
          formData.alinan_faturalar = [];
          formData.alinan_fatura_toplam = 0;
        }
        // Fatura belgesi alinan_faturalar içine taşındı, belgeler dizisinden kaldır (çoğaltmayı önle)
        if (faturaBelgesi && faturaMiktar > 0) {
          formData.belgeler = form.belgeler.slice(1); // İlk belgeyi çıkar, geri kalanları tut
        }
        console.log('Fatura ekleniyor:', formData.alinan_faturalar);
      }

      // ONAYLANAN İŞ AKIŞI: Gerçek kaydı YAZMA! Sadece Taslak (pending_transaction) olarak ekle.
      // Gerçek Çıkış kaydı, Yönetim Kurulu onayladıktan sonra oluşturulacak.
      if (pendingJobAction && !editingCikis) {
        try {
          const pendingPayload = {
            type: 'cikis',
            tarih: formData.tarih,
            tutar: parseFloat(formData.miktar) || 0,
            para_birimi: formData.para_birimi || 'TL',
            cari_hesap_id: formData.cari_hesap_id || null,
            cari_hesap_adi: formData.cari_hesap_adi || '',
            hesap_id: formData.hesap_id || null,
            hesap_adi: formData.hesap_adi || '',
            hesap_tipi: formData.hesap_tipi || null,
            aciklama: formData.aciklama || '',
            belgeler: formData.belgeler || [],
            form_data: { ...formData }
          };
          const pendingRes = await fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/accounting/isler/${pendingJobAction.job_id}/pending-transactions`,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
              body: JSON.stringify(pendingPayload)
            }
          );
          if (!pendingRes.ok) {
            const err = await pendingRes.json().catch(() => ({}));
            toast.error(err.detail || 'Taslak işlem eklenemedi');
            return;
          }
          // İşi Hazırlanan İşler'e taşı (YK onayı için)
          await fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/accounting/isler/${pendingJobAction.job_id}/complete-approved`,
            {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
              body: JSON.stringify({ note: 'Çıkış kaydı taslak olarak eklendi' })
            }
          );
          toast.success(`"${pendingJobAction.job_konu}" işi için çıkış taslağı oluşturuldu ve Yönetim Kurulu onayına gönderildi`);
          setPendingJobAction(null);
          localStorage.removeItem('pending_job_action');
          setShowModal(false);
          navigate('/accounting/jobs');
          return;
        } catch (e) {
          console.error('Taslak çıkış ekleme hatası:', e);
          toast.error('Taslak çıkış eklenemedi');
          return;
        }
      }

      const response = await fetch(url, {
        method: editingCikis ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        const responseData = await response.json();
        toast.success(editingCikis ? 'Çıkış güncellendi' : 'Çıkış eklendi');
        
        if (form.cek_karsiligi && form.cek_id && !editingCikis) {
          toast.success('Çek ödenmiş olarak işaretlendi');
        }
        
        // Mark borc payment as paid if selected
        if (form.takipteki_borc && form.borc_takip_id && form.borc_odeme_id && !editingCikis) {
          try {
            const borcResponse = await fetch(
              `${process.env.REACT_APP_BACKEND_URL}/api/accounting/takipler/borc/${form.borc_takip_id}/odeme/${form.borc_odeme_id}`,
              {
                method: 'PUT',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                  odendi: true,
                  odeme_tarihi: formData.tarih,
                  tutar: formData.miktar
                })
              }
            );
            if (borcResponse.ok) {
              toast.success('Borç takibi güncellendi');
            }
          } catch (e) {
            console.error('Borç takibi güncellenemedi:', e);
          }
        }
        
        // Eğer Borç Takip oluştur seçildiyse
        if (form.create_takip && !editingCikis) {
          try {
            const odemeler = [
              {
                tarih: form.tarih,
                tutar: form.miktar,
                odeme_sekli: form.hesap_tipi || 'havale_eft',
                aciklama: 'Peşinat / İlk Ödeme (Çıkış İşlemi)',
                odendi: true,
                odenen_tutar: form.miktar,
                odeme_tarihi: form.tarih
              },
              ...form.takip_odemeler.filter(o => o.tutar && o.tarih)
            ];

            const takipPayload = {
              olusturma_tarihi: form.tarih,
              cari_hesap_id: form.cari_hesap_id,
              cari_hesap_adi: form.cari_hesap_adi,
              etiket_id: form.etiket_id,
              etiket_adi: form.etiket_adi,
              fortis_sube_id: '',
              fortis_sube: form.fortis_sube,
              aciklama: form.aciklama,
              para_birimi: form.para_birimi,
              toplam_tutar: form.takip_toplam_tutar,
              fatura_miktari: form.takip_fatura_miktari,
              kdv_detaylari: form.takip_kdv_detaylari.filter(k => k.miktar && k.kdv_tutari),
              odemeler: odemeler
            };
            
            const takipRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/accounting/takipler/borc`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify(takipPayload)
            });
            
            if (takipRes.ok) {
              toast.success('Borç Takibi başarıyla oluşturuldu');
            } else {
              toast.error('Borç Takibi oluşturulurken bir hata oluştu');
            }
          } catch (error) {
            console.error('Borç Takibi oluşturma hatası:', error);
            toast.error('Borç Takibi oluşturulamadı');
          }
        }
        
        setShowModal(false);
        fetchCikislar();
        fetchDropdownData();
      } else {
        toast.error('İşlem başarısız');
      }
    } catch (error) {
      toast.error('Bir hata oluştu');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu çıkış kaydını silmek istediğinizden emin misiniz?')) return;

    try {
      const token = localStorage.getItem('fortis_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/accounting/cikislar/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Çıkış silindi');
        fetchCikislar();
      }
    } catch (error) {
      toast.error('Silme işlemi başarısız');
    }
  };

  // Fatura Ekleme - Alınan Fatura
  const openFaturaModal = () => {
    setFaturaForm({
      fatura_miktari: '',
      kdv_tutari: '',
      fatura_belgesi: null,
      fatura_tarihi: new Date().toISOString().split('T')[0]
    });
    setShowFaturaModal(true);
  };

  const handleFaturaFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (event) => {
      setFaturaForm(prev => ({
        ...prev,
        fatura_belgesi: {
          url: event.target.result,
          name: file.name
        }
      }));
    };
    reader.readAsDataURL(file);
    toast.success('Fatura belgesi eklendi');
    e.target.value = '';
  };

  const handleFaturaSubmit = async () => {
    if (!faturaForm.fatura_miktari) {
      toast.error('Lütfen fatura miktarını girin');
      return;
    }
    
    if (!faturaForm.fatura_belgesi) {
      toast.error('Lütfen fatura belgesini yükleyin');
      return;
    }

    setFaturaUploading(true);
    try {
      const token = localStorage.getItem('fortis_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/accounting/cikislar/${detailRecord.id}/fatura`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(faturaForm)
      });

      if (response.ok) {
        toast.success('Alınan fatura eklendi');
        setShowFaturaModal(false);
        setShowDetailModal(false);
        fetchCikislar();
      } else {
        toast.error('Fatura eklenemedi');
      }
    } catch (error) {
      toast.error('Fatura ekleme hatası');
    } finally {
      setFaturaUploading(false);
    }
  };

  // Fatura durumu güncelleme fonksiyonu
  const handleFaturaDurumuChange = async (newDurum) => {
    try {
      const token = localStorage.getItem('fortis_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/accounting/cikislar/${detailRecord.id}/fatura-durumu`, {
        method: 'PATCH',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ fatura_durumu: newDurum })
      });

      if (response.ok) {
        toast.success('Fatura durumu güncellendi');
        setDetailRecord(prev => ({ ...prev, fatura_durumu: newDurum }));
        fetchCikislar();
      } else {
        toast.error('Fatura durumu güncellenemedi');
      }
    } catch (error) {
      toast.error('Fatura durumu güncelleme hatası');
    }
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    const newBelgeler = [];
    for (const file of files) {
      const reader = new FileReader();
      const fileData = await new Promise((resolve) => {
        reader.onload = (event) => {
          resolve({
            url: event.target.result,
            name: file.name
          });
        };
        reader.readAsDataURL(file);
      });
      newBelgeler.push(fileData);
    }
    
    setForm(prev => ({
      ...prev,
      belgeler: [...prev.belgeler, ...newBelgeler]
    }));
    toast.success(`${files.length} belge eklendi`);
    e.target.value = ''; // Reset input
  };

  const removeBelge = (index) => {
    setForm(prev => ({
      ...prev,
      belgeler: prev.belgeler.filter((_, i) => i !== index)
    }));
  };

  const handleCekSelect = (cek) => {
    setForm({
      ...form,
      cek_id: cek.id,
      cek_no: cek.cek_no,
      miktar: cek.tutar || form.miktar
    });
    setCekOpen(false);
  };

  const handleBorcOdemeSelect = (odeme) => {
    // Kalan tutarı miktar olarak ayarla (kısmi ödeme için)
    const kalanTutar = odeme.kalan_tutar !== undefined ? odeme.kalan_tutar : odeme.tutar;
    setForm({
      ...form,
      borc_takip_id: odeme.takip_id,
      borc_odeme_id: odeme.odeme_id,
      borc_odeme_text: odeme.display_text,
      miktar: kalanTutar || form.miktar,
      cari_hesap_adi: odeme.cari_hesap_adi || form.cari_hesap_adi
    });
    setBorcOdemeOpen(false);
  };

  // Calculate totals by currency (excluding credit card and nakit_kasa for TL)
  const totalsByCurrency = useMemo(() => {
    const totals = { TL: 0, USD: 0, EUR: 0 };
    filteredAndSortedCikislar.forEach(c => {
      // Kredi kartı ve nakit kasa harcamalarını TL toplamına dahil etme
      if (c.hesap_tipi === 'kredi_karti' || c.hesap_tipi === 'nakit_kasa') return;
      const currency = c.para_birimi || 'TL';
      totals[currency] = (totals[currency] || 0) + (parseFloat(c.miktar) || 0);
    });
    return totals;
  }, [filteredAndSortedCikislar]);

  // Calculate credit card totals
  const krediKartiToplam = useMemo(() => {
    return filteredAndSortedCikislar
      .filter(c => c.hesap_tipi === 'kredi_karti')
      .reduce((sum, c) => sum + (parseFloat(c.miktar) || 0), 0);
  }, [filteredAndSortedCikislar]);

  // Calculate nakit kasa (cash account) totals - Kasalardan Harcanan
  const kasalardanHarcananToplam = useMemo(() => {
    return filteredAndSortedCikislar
      .filter(c => c.hesap_tipi === 'nakit_kasa')
      .reduce((sum, c) => sum + (parseFloat(c.miktar) || 0), 0);
  }, [filteredAndSortedCikislar]);

  // Calculate detailed breakdown by each kasa
  const kasaBazindaHarcamalar = useMemo(() => {
    const kasaMap = {};
    filteredAndSortedCikislar
      .filter(c => c.hesap_tipi === 'nakit_kasa')
      .forEach(c => {
        const kasaAdi = c.hesap_adi || 'Bilinmeyen Kasa';
        if (!kasaMap[kasaAdi]) {
          kasaMap[kasaAdi] = 0;
        }
        kasaMap[kasaAdi] += parseFloat(c.miktar) || 0;
      });
    // Convert to array and sort by amount descending
    return Object.entries(kasaMap)
      .map(([kasa, tutar]) => ({ kasa, tutar }))
      .sort((a, b) => b.tutar - a.tutar);
  }, [filteredAndSortedCikislar]);

  const totalAmount = filteredAndSortedCikislar.reduce((sum, c) => sum + (parseFloat(c.miktar) || 0), 0);

  const formatCurrency = (amount, currency = 'TL') => {
    const currencyMap = {
      'TL': 'TRY',
      'USD': 'USD',
      'EUR': 'EUR'
    };
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: currencyMap[currency] || 'TRY'
    }).format(amount);
  };

  const getCurrencySymbol = (currency) => {
    const symbols = { 'TL': '₺', 'USD': '$', 'EUR': '€' };
    return symbols[currency] || '₺';
  };

  // Get unique values for filters
  const uniqueHesaplar = [...new Set(cikislar.map(c => c.hesap_adi).filter(Boolean))];
  const uniqueSubeler = [...new Set(cikislar.map(c => c.fortis_sube).filter(Boolean))];
  const uniqueEtiketler = [...new Set(cikislar.map(c => c.etiket_adi).filter(Boolean))];

  return (
    <div className="space-y-6" data-testid="accounting-expenses">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Çıkışlar</h1>
          <p className="text-gray-500">Gider kayıtları ve takibi</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setShowFilters(!showFilters)}
            className={cn("gap-2", showFilters && "bg-orange-50 border-orange-300")}
            data-testid="toggle-filters-btn"
          >
            <Filter className="h-4 w-4" />
            Filtrele
          </Button>
          <Button
            variant="outline"
            onClick={exportToExcel}
            className="gap-2"
            data-testid="export-excel-btn"
          >
            <Download className="h-4 w-4" />
            Excel İndir
          </Button>
          <Button
            onClick={openNewModal}
            className="gap-2 bg-orange-600 hover:bg-orange-700"
            data-testid="add-cikis-btn"
          >
            <Plus className="h-4 w-4" />
            Çıkış Ekle
          </Button>
        </div>
      </div>

      {/* Month Navigation */}
      <div className="flex items-center justify-center gap-4 py-3 bg-gradient-to-r from-orange-50 to-amber-50 rounded-lg border border-orange-200">
        <Button
          variant="outline"
          size="sm"
          onClick={goToPreviousMonth}
          className="gap-1 border-orange-300 hover:bg-orange-100"
          data-testid="prev-month-btn"
        >
          <ChevronLeft className="h-4 w-4" />
          Önceki
        </Button>
        <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-lg border border-orange-300 shadow-sm">
          <Calendar className="h-5 w-5 text-orange-600" />
          <span className="font-semibold text-orange-800 min-w-[120px] text-center">
            {monthNames[currentMonth.month]} {currentMonth.year}
          </span>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={goToNextMonth}
          className="gap-1 border-orange-300 hover:bg-orange-100"
          data-testid="next-month-btn"
        >
          Sonraki
          <ChevronRight className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={goToCurrentMonth}
          className="text-orange-600 hover:text-orange-800 hover:bg-orange-100"
          data-testid="current-month-btn"
        >
          Bugün
        </Button>
      </div>

      {/* Day Selector Grid */}
      <div className="bg-white rounded-lg border border-gray-200 p-3">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs text-gray-500">
            {selectedDay ? `${selectedDay} ${monthNames[currentMonth.month]} ${currentMonth.year} seçili` : 'Tüm ay gösteriliyor'}
          </span>
          {selectedDay && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelectedDay(null)}
              className="text-xs h-6 px-2 text-gray-500 hover:text-gray-700"
            >
              Tüm Ayı Göster
            </Button>
          )}
        </div>
        {/* Day names header */}
        <div className="grid grid-cols-7 gap-1 mb-1">
          {dayNames.map(day => (
            <div key={day} className="text-center text-xs font-medium text-gray-500 py-1">
              {day}
            </div>
          ))}
        </div>
        {/* Days grid */}
        <div className="grid grid-cols-7 gap-1">
          {/* Empty cells for days before month starts */}
          {Array.from({ length: getDaysInMonth.firstDay }).map((_, idx) => (
            <div key={`empty-${idx}`} className="h-8" />
          ))}
          {/* Day buttons */}
          {Array.from({ length: getDaysInMonth.daysInMonth }).map((_, idx) => {
            const day = idx + 1;
            const isToday = new Date().getDate() === day && 
                           new Date().getMonth() === currentMonth.month && 
                           new Date().getFullYear() === currentMonth.year;
            const isSelected = selectedDay === day;
            const isWeekend = ((getDaysInMonth.firstDay + idx) % 7) >= 5;
            
            // Check if this day has any entries
            const dayStr = `${currentMonth.year}-${String(currentMonth.month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            const hasEntries = cikislar.some(c => c.tarih && c.tarih.startsWith(dayStr));
            
            return (
              <button
                key={day}
                onClick={() => selectDay(day)}
                className={cn(
                  "h-8 text-sm rounded-md transition-all relative",
                  isSelected 
                    ? "bg-orange-600 text-white font-bold shadow-md" 
                    : isToday 
                      ? "bg-orange-100 text-orange-800 font-semibold border-2 border-orange-400"
                      : hasEntries
                        ? "bg-orange-50 text-gray-700 hover:bg-orange-100 font-medium"
                        : isWeekend
                          ? "bg-gray-50 text-gray-400 hover:bg-gray-100"
                          : "bg-white text-gray-600 hover:bg-orange-50",
                  "focus:outline-none focus:ring-2 focus:ring-orange-400"
                )}
                data-testid={`day-${day}`}
              >
                {day}
                {hasEntries && !isSelected && (
                  <span className="absolute bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 bg-orange-500 rounded-full" />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Filter Panel */}
      {showFilters && (
        <Card className="bg-orange-50 border-orange-200">
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-7 gap-4">
              <div className="space-y-2">
                <Label className="text-xs">Başlangıç Tarihi</Label>
                <Input
                  type="date"
                  value={filterDateFrom}
                  onChange={(e) => setFilterDateFrom(e.target.value)}
                  className="bg-white"
                  data-testid="filter-date-from"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs">Bitiş Tarihi</Label>
                <Input
                  type="date"
                  value={filterDateTo}
                  onChange={(e) => setFilterDateTo(e.target.value)}
                  className="bg-white"
                  data-testid="filter-date-to"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs">Cari Hesap</Label>
                <Input
                  placeholder="Cari hesap ara..."
                  value={filterCariHesap}
                  onChange={(e) => setFilterCariHesap(e.target.value)}
                  className="bg-white"
                  data-testid="filter-cari"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs">Hesap</Label>
                <Select value={filterHesap} onValueChange={setFilterHesap}>
                  <SelectTrigger className="bg-white" data-testid="filter-hesap">
                    <SelectValue placeholder="Tümü" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all_hesaplar">Tümü</SelectItem>
                    {uniqueHesaplar.map((hesap) => (
                      <SelectItem key={hesap} value={hesap}>{hesap}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs">Şube</Label>
                <Select value={filterSube} onValueChange={setFilterSube}>
                  <SelectTrigger className="bg-white" data-testid="filter-sube">
                    <SelectValue placeholder="Tümü" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all_branches">Tümü</SelectItem>
                    {uniqueSubeler.map((sube) => (
                      <SelectItem key={sube} value={sube}>{sube}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs">Etiket</Label>
                <Select value={filterEtiket} onValueChange={setFilterEtiket}>
                  <SelectTrigger className="bg-white" data-testid="filter-etiket">
                    <SelectValue placeholder="Tümü" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all_tags">Tümü</SelectItem>
                    {uniqueEtiketler.map((etiket) => (
                      <SelectItem key={etiket} value={etiket}>{etiket}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs">Çek Karşılığı</Label>
                <Select value={filterCekKarsiligi} onValueChange={setFilterCekKarsiligi}>
                  <SelectTrigger className="bg-white" data-testid="filter-cek">
                    <SelectValue placeholder="Tümü" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tümü</SelectItem>
                    <SelectItem value="yes">Evet</SelectItem>
                    <SelectItem value="no">Hayır</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="mt-4 flex justify-end">
              <Button variant="ghost" onClick={clearFilters} className="text-sm">
                Filtreleri Temizle
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-orange-500 rounded-lg shrink-0">
                <ArrowUpCircle className="h-6 w-6 text-white" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm text-orange-600 font-medium">Gösterilen Çıkış</p>
                <p className="text-xl font-bold text-orange-900">{filteredAndSortedCikislar.length}</p>
                {filteredAndSortedCikislar.length !== cikislar.length && (
                  <p className="text-xs text-orange-500">Toplam: {cikislar.length}</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-red-500 rounded-lg shrink-0">
                <FileText className="h-6 w-6 text-white" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm text-red-600 font-medium">Toplam TL</p>
                <p className="text-base font-bold text-red-900 whitespace-nowrap">{formatCurrency(totalsByCurrency.TL, 'TL')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        {totalsByCurrency.USD > 0 && (
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-blue-500 rounded-lg shrink-0">
                  <FileText className="h-6 w-6 text-white" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-blue-600 font-medium">Toplam USD</p>
                  <p className="text-base font-bold text-blue-900 whitespace-nowrap">{formatCurrency(totalsByCurrency.USD, 'USD')}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
        {totalsByCurrency.EUR > 0 && (
          <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-purple-500 rounded-lg shrink-0">
                  <FileText className="h-6 w-6 text-white" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-purple-600 font-medium">Toplam EUR</p>
                  <p className="text-base font-bold text-purple-900 whitespace-nowrap">{formatCurrency(totalsByCurrency.EUR, 'EUR')}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
        {krediKartiToplam > 0 && (
          <Card className="bg-gradient-to-br from-indigo-50 to-indigo-100 border-indigo-200">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-indigo-500 rounded-lg shrink-0">
                  <CreditCard className="h-6 w-6 text-white" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-indigo-600 font-medium">Kredi Kartı</p>
                  <p className="text-base font-bold text-indigo-900 whitespace-nowrap">{formatCurrency(krediKartiToplam, 'TL')}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
        {kasalardanHarcananToplam > 0 && (
          <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
            <CardContent className="pt-6 pb-4">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-emerald-500 rounded-lg shrink-0">
                  <Wallet className="h-6 w-6 text-white" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-emerald-600 font-medium">Kasalardan Harcanan</p>
                  <p className="text-base font-bold text-emerald-900 whitespace-nowrap">{formatCurrency(kasalardanHarcananToplam, 'TL')}</p>
                </div>
              </div>
              {/* Kasa bazında detay */}
              {kasaBazindaHarcamalar.length > 0 && (
                <div className="mt-3 pt-3 border-t border-emerald-200 space-y-1">
                  {kasaBazindaHarcamalar.map((item, idx) => (
                    <div key={idx} className="flex justify-between items-center text-xs">
                      <span className="text-emerald-700 truncate max-w-[120px]" title={item.kasa}>
                        💵 {item.kasa}
                      </span>
                      <span className="font-medium text-emerald-800">{formatCurrency(item.tutar, 'TL')}</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <ArrowUpCircle className="h-5 w-5 text-orange-600" />
              Gider Kayıtları
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          {/* Search */}
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Cari hesap, açıklama, çek no veya şube ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="cikis-search"
            />
          </div>

          {/* Table */}
          <div className="rounded-lg border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead 
                    className="cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('seri_no')}
                  >
                    <div className="flex items-center">
                      Seri No
                      {getSortIcon('seri_no')}
                    </div>
                  </TableHead>
                  {isBoardView && (
                    <TableHead className="text-center w-16">Onay</TableHead>
                  )}
                  <TableHead 
                    className="cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('tarih')}
                  >
                    <div className="flex items-center">
                      Tarih/Saat
                      {getSortIcon('tarih')}
                    </div>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('cari_hesap_adi')}
                  >
                    <div className="flex items-center">
                      Cari Hesap
                      {getSortIcon('cari_hesap_adi')}
                    </div>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('miktar')}
                  >
                    <div className="flex items-center">
                      Miktar
                      {getSortIcon('miktar')}
                    </div>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('hesap_adi')}
                  >
                    <div className="flex items-center">
                      Hesap
                      {getSortIcon('hesap_adi')}
                    </div>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('cek_no')}
                  >
                    <div className="flex items-center">
                      Çek
                      {getSortIcon('cek_no')}
                    </div>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('fortis_sube')}
                  >
                    <div className="flex items-center">
                      Şube
                      {getSortIcon('fortis_sube')}
                    </div>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('etiket_adi')}
                  >
                    <div className="flex items-center">
                      Etiket
                      {getSortIcon('etiket_adi')}
                    </div>
                  </TableHead>
                  <TableHead>Belgeler</TableHead>
                  <TableHead>Anlaşılan Fatura</TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('fatura_durumu')}
                  >
                    <div className="flex items-center">
                      Fatura Durumu
                      {getSortIcon('fatura_durumu')}
                    </div>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('alinan_fatura_toplam')}
                  >
                    <div className="flex items-center">
                      Alınan Fatura
                      {getSortIcon('alinan_fatura_toplam')}
                    </div>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('aciklama')}
                  >
                    <div className="flex items-center">
                      Açıklama
                      {getSortIcon('aciklama')}
                    </div>
                  </TableHead>
                  <TableHead className="text-center">İş Kaydı</TableHead>
                  <TableHead className="text-right">Detay</TableHead>
                  {isBoardView && (
                    <TableHead className="text-center">İşlemler</TableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredAndSortedCikislar.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={isBoardView ? 17 : 15} className="text-center py-8 text-gray-500">
                      Çıkış kaydı bulunamadı
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredAndSortedCikislar.map((cikis) => (
                    <TableRow key={cikis.id} data-testid={`cikis-row-${cikis.id}`}>
                      <TableCell className="font-mono text-sm text-gray-600">
                        {cikis.seri_no || '-'}
                      </TableCell>
                      {isBoardView && (
                        <TableCell className="text-center">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openApprovalModal(cikis)}
                            className={`h-8 w-8 p-0 rounded-full ${
                              cikis.approval_status === 'dogru' ? 'bg-green-100 hover:bg-green-200' :
                              cikis.approval_status === 'yanlis' ? 'bg-red-100 hover:bg-red-200' :
                              cikis.approval_status === 'supheli' ? 'bg-amber-100 hover:bg-amber-200' :
                              'hover:bg-gray-100'
                            }`}
                            title={cikis.approval_status ? `${cikis.approval_by} tarafından incelendi` : 'İnceleme ekle'}
                          >
                            {getApprovalIcon(cikis.approval_status)}
                          </Button>
                        </TableCell>
                      )}
                      <TableCell className="font-medium">
                        {cikis.tarih}<br />
                        <span className="text-xs text-gray-500">{cikis.saat}</span>
                      </TableCell>
                      <TableCell>{cikis.cari_hesap_adi || '-'}</TableCell>
                      <TableCell className="font-semibold text-red-600">
                        {formatCurrency(cikis.miktar, cikis.para_birimi || 'TL')}
                      </TableCell>
                      <TableCell>
                        <span className="text-xs">
                          {cikis.hesap_tipi === 'kredi_karti' ? '💳' : cikis.hesap_tipi === 'nakit_kasa' ? '💵' : '🏦'} {cikis.hesap_adi || '-'}
                        </span>
                      </TableCell>
                      <TableCell>
                        {cikis.cek_karsiligi ? (
                          <Badge className="bg-indigo-100 text-indigo-700 hover:bg-indigo-100">
                            <FileCheck className="h-3 w-3 mr-1" />
                            {cikis.cek_no}
                          </Badge>
                        ) : '-'}
                      </TableCell>
                      <TableCell>{cikis.fortis_sube || '-'}</TableCell>
                      <TableCell>
                        {cikis.etiket_adi && (
                          <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs">
                            {cikis.etiket_adi}
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        {(() => {
                          // Tüm belgeleri birleştir: normal belgeler + fatura belgeleri
                          const allBelgeler = [];
                          
                          // Normal belgeler
                          if (cikis.belgeler && cikis.belgeler.length > 0) {
                            cikis.belgeler.forEach((b, i) => allBelgeler.push({ ...b, type: 'Belge', index: i + 1 }));
                          }
                          
                          // Dekont
                          if (cikis.dekont_url) {
                            allBelgeler.push({ url: cikis.dekont_url, name: cikis.dekont_adi || 'Dekont', type: 'Dekont' });
                          }
                          
                          // Alınan fatura belgeleri
                          if (cikis.alinan_faturalar && cikis.alinan_faturalar.length > 0) {
                            cikis.alinan_faturalar.forEach((f, i) => {
                              if (f.belge) {
                                allBelgeler.push({ 
                                  ...f.belge, 
                                  type: 'Alınan Fatura',
                                  name: f.belge.name || `Fatura ${i + 1} - ${f.miktar ? `₺${f.miktar.toLocaleString('tr-TR')}` : ''}`,
                                  tarih: f.tarih
                                });
                              }
                            });
                          }
                          
                          if (allBelgeler.length > 0) {
                            return (
                              <button
                                onClick={() => {
                                  setPreviewBelgeler(allBelgeler);
                                  setShowDocPreview(true);
                                }}
                                className="flex items-center gap-1 cursor-pointer hover:underline"
                                data-testid={`belgeler-link-${cikis.id}`}
                              >
                                <FileText className="h-4 w-4 text-blue-600" />
                                <span className="text-xs text-blue-600">{allBelgeler.length} belge</span>
                              </button>
                            );
                          }
                          return '-';
                        })()}
                      </TableCell>
                      <TableCell className="text-right">
                        {cikis.anlaşılan_fatura_miktari ? (
                          <span className="font-medium text-purple-600">
                            ₺{parseFloat(cikis.anlaşılan_fatura_miktari).toLocaleString('tr-TR', {minimumFractionDigits: 2})}
                          </span>
                        ) : '-'}
                      </TableCell>
                      <TableCell>
                        {cikis.fatura_durumu ? (
                          <div className="space-y-1">
                            <Badge className={
                              cikis.fatura_durumu === 'fatura_alindi' ? 'bg-green-100 text-green-700 hover:bg-green-100' :
                              cikis.fatura_durumu === 'borc_verildi' ? 'bg-orange-100 text-orange-700 hover:bg-orange-100' :
                              cikis.fatura_durumu === 'borc_odendi' ? 'bg-blue-100 text-blue-700 hover:bg-blue-100' :
                              cikis.fatura_durumu === 'fis' ? 'bg-purple-100 text-purple-700 hover:bg-purple-100' :
                              cikis.fatura_durumu === 'faturasiz_nakit' ? 'bg-gray-100 text-gray-700 hover:bg-gray-100' :
                              cikis.fatura_durumu === 'eksik_fatura' ? 'bg-red-100 text-red-700 hover:bg-red-100' :
                              'bg-yellow-100 text-yellow-700 hover:bg-yellow-100'
                            }>
                              {cikis.fatura_durumu === 'fatura_alindi' ? 'Fatura Alındı' :
                               cikis.fatura_durumu === 'borc_verildi' ? 'Borç Verildi' :
                               cikis.fatura_durumu === 'borc_odendi' ? 'Borç Ödendi' : 
                               cikis.fatura_durumu === 'fis' ? 'Fiş' :
                               cikis.fatura_durumu === 'faturasiz_nakit' ? 'Faturasız Nakit' : 
                               cikis.fatura_durumu === 'eksik_fatura' ? 'Eksik Fatura' : 'Fatura Kesilecek'}
                            </Badge>
                            {cikis.fatura_durumu === 'eksik_fatura' ? (
                              <div className="text-xs text-red-600 font-medium mt-1">
                                Eksik: {formatCurrency(parseFloat(cikis.anlaşılan_fatura_miktari || cikis.fatura_miktari || 0) - parseFloat(cikis.alinan_fatura_toplam || 0))}
                              </div>
                            ) : ['fatura_alindi', 'fis'].includes(cikis.fatura_durumu) && cikis.fatura_miktari && (
                              <div className="text-xs text-green-600 font-medium mt-1">
                                {formatCurrency(cikis.fatura_miktari)}
                              </div>
                            )}
                            {cikis.alinan_fatura_eklendi && (
                              <div className="text-xs text-orange-600 font-medium">
                                ✓ Fatura: {formatCurrency(cikis.alinan_fatura_miktari)}
                              </div>
                            )}
                          </div>
                        ) : '-'}
                      </TableCell>
                      <TableCell>
                        {cikis.alinan_fatura_toplam > 0 ? (
                          <span className="font-medium text-orange-600">
                            {formatCurrency(cikis.alinan_fatura_toplam, cikis.para_birimi)}
                          </span>
                        ) : cikis.alinan_faturalar && cikis.alinan_faturalar.length > 0 ? (
                          <span className="font-medium text-orange-600">
                            {formatCurrency(cikis.alinan_faturalar.reduce((sum, f) => sum + (f.miktar || 0), 0), cikis.para_birimi)}
                          </span>
                        ) : ['fatura_alindi', 'eksik_fatura', 'fis'].includes(cikis.fatura_durumu) && cikis.fatura_miktari ? (
                          <span className="font-medium text-orange-600">
                            {formatCurrency(cikis.fatura_miktari, cikis.para_birimi)}
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <span className="text-xs text-gray-600 max-w-[150px] truncate block" title={cikis.aciklama}>
                          {cikis.aciklama || '-'}
                        </span>
                      </TableCell>
                      <TableCell className="text-center">
                        {cikis.job_id ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedJobId(cikis.job_id);
                              setShowJobPopup(true);
                            }}
                            className="text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50"
                            title="İş Kaydını Görüntüle"
                          >
                            <Briefcase className="h-4 w-4" />
                          </Button>
                        ) : (
                          <span className="text-gray-300">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setDetailRecord(cikis);
                            setShowDetailModal(true);
                          }}
                          title="Detay Görüntüle"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </TableCell>
                      {isBoardView && (
                        <TableCell className="text-center">
                          <div className="flex items-center justify-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setEditingCikis(cikis);
                                setShowModal(true);
                              }}
                              title="Düzenle"
                              className="h-8 w-8 p-0 hover:bg-blue-100 hover:text-blue-600"
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                if (window.confirm('Bu çıkış kaydını silmek istediğinize emin misiniz?')) {
                                  handleDelete(cikis.id);
                                }
                              }}
                              title="Sil"
                              className="h-8 w-8 p-0 hover:bg-red-100 hover:text-red-600"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Add/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowUpCircle className="h-5 w-5 text-orange-600" />
              {editingCikis ? 'Çıkış Düzenle' : 'Çıkış Ekle'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Date and Time */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="tarih">Tarih</Label>
                <Input
                  id="tarih"
                  type="date"
                  value={form.tarih}
                  onChange={(e) => setForm({ ...form, tarih: e.target.value })}
                  data-testid="cikis-tarih"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="saat">Saat</Label>
                <Input
                  id="saat"
                  type="time"
                  value={form.saat}
                  onChange={(e) => setForm({ ...form, saat: e.target.value })}
                  data-testid="cikis-saat"
                />
              </div>
            </div>

            {/* Cari Hesap */}
            <div className="space-y-2">
              <Label>Cari Hesap</Label>
              <Popover open={cariOpen} onOpenChange={setCariOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={cariOpen}
                    className="w-full justify-between font-normal"
                    data-testid="cikis-cari-select"
                  >
                    {form.cari_hesap_adi || "Cari hesap seçin..."}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Cari hesap veya kasa ara..." />
                    <CommandList>
                      <CommandEmpty>Cari hesap veya kasa bulunamadı.</CommandEmpty>
                      {/* Nakit Kasalar - Merkez Kasaları */}
                      {nakitKasalar.length > 0 && (
                        <CommandGroup heading="Merkez Kasaları">
                          {nakitKasalar.map((kasa) => (
                            <CommandItem
                              key={`kasa-${kasa.id}`}
                              value={`${kasa.kasa_adi} kasa`}
                              onSelect={() => {
                                setForm({
                                  ...form,
                                  cari_hesap_id: kasa.id,
                                  cari_hesap_adi: kasa.kasa_adi
                                });
                                setCariOpen(false);
                              }}
                            >
                              <Check
                                className={cn(
                                  "mr-2 h-4 w-4",
                                  form.cari_hesap_id === kasa.id ? "opacity-100" : "opacity-0"
                                )}
                              />
                              <Wallet className="mr-2 h-4 w-4 text-green-600" />
                              {kasa.kasa_adi}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      )}
                      {/* Cari Hesaplar */}
                      <CommandGroup heading="Cari Hesaplar">
                        {cariHesaplar.map((cari) => (
                          <CommandItem
                            key={cari.id}
                            value={cari.ad}
                            onSelect={() => {
                              setForm({
                                ...form,
                                cari_hesap_id: cari.id,
                                cari_hesap_adi: cari.ad
                              });
                              setCariOpen(false);
                            }}
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4",
                                form.cari_hesap_id === cari.id ? "opacity-100" : "opacity-0"
                              )}
                            />
                            {cari.ad} {cari.il && `(${cari.il})`}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            {/* Çek Karşılığı */}
            <div className="space-y-2 p-4 bg-orange-50 rounded-lg border border-orange-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FileCheck className="h-5 w-5 text-orange-600" />
                  <Label htmlFor="cek_karsiligi" className="font-medium text-orange-900">Çek Karşılığı</Label>
                </div>
                <Switch
                  id="cek_karsiligi"
                  checked={form.cek_karsiligi}
                  onCheckedChange={(checked) => {
                    setForm({ 
                      ...form, 
                      cek_karsiligi: checked,
                      cek_id: checked ? form.cek_id : '',
                      cek_no: checked ? form.cek_no : ''
                    });
                  }}
                  data-testid="cikis-cek-karsiligi-switch"
                />
              </div>
              <p className="text-xs text-orange-600">
                {form.cek_karsiligi ? 'Evet - Bir çek seçin' : 'Hayır - Çek karşılığı değil'}
              </p>
              
              {form.cek_karsiligi && (
                <div className="mt-3 space-y-2">
                  <Label>Çek Seçin (Ödenecek Verilen Çekler)</Label>
                  <Popover open={cekOpen} onOpenChange={setCekOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={cekOpen}
                        className="w-full justify-between font-normal bg-white"
                        data-testid="cikis-cek-select"
                      >
                        {form.cek_no ? `Çek No: ${form.cek_no}` : "Çek seçin..."}
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-full p-0" align="start">
                      <Command>
                        <CommandInput placeholder="Çek no ara..." />
                        <CommandList>
                          <CommandEmpty>Ödenecek çek bulunamadı.</CommandEmpty>
                          <CommandGroup heading="Verilen Ödenecek Çekler">
                            {odenecekCekler.map((cek) => (
                              <CommandItem
                                key={cek.id}
                                value={`${cek.cek_no} ${cek.banka} ${cek.alici}`}
                                onSelect={() => handleCekSelect(cek)}
                              >
                                <Check
                                  className={cn(
                                    "mr-2 h-4 w-4",
                                    form.cek_id === cek.id ? "opacity-100" : "opacity-0"
                                  )}
                                />
                                <div className="flex flex-col">
                                  <span className="font-medium">{cek.cek_no} - {formatCurrency(cek.tutar)}</span>
                                  <span className="text-xs text-gray-500">
                                    {cek.banka} | Vade: {cek.vade_tarihi ? new Date(cek.vade_tarihi).toLocaleDateString('tr-TR') : '-'}
                                  </span>
                                </div>
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                  {odenecekCekler.length === 0 && (
                    <p className="text-xs text-amber-600">Ödenecek çek bulunamadı.</p>
                  )}
                </div>
              )}
            </div>

            {/* Takipteki Borç */}
            <div className="space-y-2 p-4 bg-red-50 rounded-lg border border-red-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <ArrowUpCircle className="h-5 w-5 text-red-600" />
                  <Label htmlFor="takipteki_borc" className="font-medium text-red-900">Takipteki Borç</Label>
                </div>
                <Switch
                  id="takipteki_borc"
                  checked={form.takipteki_borc}
                  onCheckedChange={(checked) => {
                    setForm({ 
                      ...form, 
                      takipteki_borc: checked,
                      borc_takip_id: checked ? form.borc_takip_id : '',
                      borc_odeme_id: checked ? form.borc_odeme_id : '',
                      borc_odeme_text: checked ? form.borc_odeme_text : ''
                    });
                  }}
                  data-testid="cikis-takipteki-borc-switch"
                />
              </div>
              <p className="text-xs text-red-600">
                {form.takipteki_borc ? 'Evet - Bir borç ödemesi seçin' : 'Hayır - Takipteki borç değil'}
              </p>
              
              {form.takipteki_borc && (
                <div className="mt-3 space-y-2">
                  <Label>Borç Ödemesi Seçin (Devam Eden Ödemeler)</Label>
                  <Popover open={borcOdemeOpen} onOpenChange={setBorcOdemeOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={borcOdemeOpen}
                        className="w-full justify-between font-normal bg-white text-left"
                        data-testid="cikis-borc-odeme-select"
                      >
                        <span className="truncate">
                          {form.borc_odeme_text || "Borç ödemesi seçin..."}
                        </span>
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-full p-0" align="start">
                      <Command>
                        <CommandInput placeholder="Cari hesap veya tutar ara..." />
                        <CommandList>
                          <CommandEmpty>Devam eden borç ödemesi bulunamadı.</CommandEmpty>
                          <CommandGroup heading="Devam Eden Borç Ödemeleri">
                            {devamEdenBorclar.map((odeme) => (
                              <CommandItem
                                key={`${odeme.takip_id}-${odeme.odeme_id}`}
                                value={odeme.display_text}
                                onSelect={() => handleBorcOdemeSelect(odeme)}
                              >
                                <Check
                                  className={cn(
                                    "mr-2 h-4 w-4",
                                    form.borc_odeme_id === odeme.odeme_id ? "opacity-100" : "opacity-0"
                                  )}
                                />
                                <div className="flex flex-col">
                                  <span className="font-medium">{odeme.cari_hesap_adi}</span>
                                  <span className="text-sm text-red-600 font-semibold">
                                    Kalan: {formatCurrency(odeme.kalan_tutar || odeme.tutar)} / Toplam: {formatCurrency(odeme.tutar)}
                                  </span>
                                  <span className="text-xs text-gray-500">
                                    Ödeme {odeme.odeme_sira}/{odeme.toplam_odeme} | Tarih: {odeme.odeme_tarihi}
                                    {odeme.odenen_tutar > 0 && ` | Ödenen: ${formatCurrency(odeme.odenen_tutar)}`}
                                  </span>
                                </div>
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                  {devamEdenBorclar.length === 0 && (
                    <p className="text-xs text-amber-600">Devam eden borç ödemesi bulunamadı.</p>
                  )}
                </div>
              )}
            </div>

            {/* Miktar ve Para Birimi */}
            <div className="space-y-2">
              <Label htmlFor="miktar">Miktar *</Label>
              <div className="flex gap-2">
                <Input
                  id="miktar"
                  type="number"
                  placeholder="0.00"
                  value={form.miktar}
                  onChange={(e) => setForm({ ...form, miktar: e.target.value })}
                  className="flex-1"
                  data-testid="cikis-miktar"
                />
                <Select 
                  value={form.para_birimi} 
                  onValueChange={(val) => setForm({ ...form, para_birimi: val })}
                >
                  <SelectTrigger className="w-24" data-testid="cikis-para-birimi">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="TL">₺ TL</SelectItem>
                    <SelectItem value="USD">$ USD</SelectItem>
                    <SelectItem value="EUR">€ EUR</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Taksit Sayısı - Sadece Kredi Kartı seçiliyken */}
            {form.hesap_tipi === 'kredi_karti' && !editingCikis && (
              <div className="space-y-2">
                <Label htmlFor="taksit_sayisi">Taksit Sayısı</Label>
                <Select 
                  value={String(form.taksit_sayisi)} 
                  onValueChange={(val) => setForm({ ...form, taksit_sayisi: parseInt(val) })}
                >
                  <SelectTrigger data-testid="cikis-taksit-sayisi">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">Tek Çekim</SelectItem>
                    <SelectItem value="2">2 Taksit</SelectItem>
                    <SelectItem value="3">3 Taksit</SelectItem>
                    <SelectItem value="4">4 Taksit</SelectItem>
                    <SelectItem value="5">5 Taksit</SelectItem>
                    <SelectItem value="6">6 Taksit</SelectItem>
                    <SelectItem value="9">9 Taksit</SelectItem>
                    <SelectItem value="12">12 Taksit</SelectItem>
                  </SelectContent>
                </Select>
                {form.taksit_sayisi > 1 && form.miktar && (
                  <p className="text-sm text-gray-500">
                    Taksit tutarı: {formatCurrency(parseFloat(form.miktar) / form.taksit_sayisi, form.para_birimi)} x {form.taksit_sayisi} ay
                  </p>
                )}
              </div>
            )}

            {/* Hesap */}
            <div className="space-y-2">
              <Label>Paranın Çıktığı Hesap</Label>
              <Popover open={hesapOpen} onOpenChange={setHesapOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={hesapOpen}
                    className="w-full justify-between font-normal"
                    data-testid="cikis-hesap-select"
                  >
                    {form.hesap_adi ? (
                      <span>{form.hesap_tipi === 'kredi_karti' ? '💳' : form.hesap_tipi === 'nakit_kasa' ? '💵' : '🏦'} {form.hesap_adi}</span>
                    ) : (
                      "Hesap seçin..."
                    )}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Hesap ara..." />
                    <CommandList>
                      <CommandEmpty>Hesap bulunamadı.</CommandEmpty>
                      <CommandGroup heading="Kredi Kartları">
                        {krediKartlari.map((kart) => (
                          <CommandItem
                            key={kart.id}
                            value={`${kart.kart_basligi} ${kart.banka_ismi}`}
                            onSelect={() => {
                              setForm({
                                ...form,
                                hesap_id: kart.id,
                                hesap_adi: `${kart.kart_basligi} (${kart.banka_ismi})`,
                                hesap_tipi: 'kredi_karti'
                              });
                              setHesapOpen(false);
                            }}
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4",
                                form.hesap_id === kart.id ? "opacity-100" : "opacity-0"
                              )}
                            />
                            💳 {kart.kart_basligi} ({kart.banka_ismi})
                          </CommandItem>
                        ))}
                      </CommandGroup>
                      <CommandGroup heading="Banka Hesapları">
                        {bankaHesaplari.map((hesap) => (
                          <CommandItem
                            key={hesap.id}
                            value={`${hesap.banka_adi} ${hesap.iban}`}
                            onSelect={() => {
                              setForm({
                                ...form,
                                hesap_id: hesap.id,
                                hesap_adi: `${hesap.banka_adi} - ${hesap.iban?.slice(-4) || 'IBAN'}`,
                                hesap_tipi: 'banka_hesabi'
                              });
                              setHesapOpen(false);
                            }}
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4",
                                form.hesap_id === hesap.id ? "opacity-100" : "opacity-0"
                              )}
                            />
                            🏦 {hesap.banka_adi} - ...{hesap.iban?.slice(-4) || 'IBAN'}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                      <CommandGroup heading="Nakit Kasalar">
                        {nakitKasalar.map((kasa) => (
                          <CommandItem
                            key={kasa.id}
                            value={`${kasa.kasa_adi} ${kasa.sorumlu_kisi}`}
                            onSelect={() => {
                              setForm({
                                ...form,
                                hesap_id: kasa.id,
                                hesap_adi: `${kasa.kasa_adi} (${kasa.sorumlu_kisi})`,
                                hesap_tipi: 'nakit_kasa'
                              });
                              setHesapOpen(false);
                            }}
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4",
                                form.hesap_id === kasa.id ? "opacity-100" : "opacity-0"
                              )}
                            />
                            💵 {kasa.kasa_adi} ({kasa.sorumlu_kisi})
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            {/* Şube */}
            <div className="space-y-2">
              <Label>Fortis Şube</Label>
              <Select
                value={form.fortis_sube}
                onValueChange={(value) => setForm({ ...form, fortis_sube: value })}
              >
                <SelectTrigger data-testid="cikis-sube-select">
                  <SelectValue placeholder="Şube seçin" />
                </SelectTrigger>
                <SelectContent>
                  {branches.map((branch) => (
                    <SelectItem key={branch.id} value={branch.name}>
                      {branch.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Etiket */}
            <div className="space-y-2">
              <Label>Etiket (Hesap Kartı)</Label>
              <Popover open={etiketOpen} onOpenChange={setEtiketOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={etiketOpen}
                    className="w-full justify-between font-normal"
                    data-testid="cikis-etiket-select"
                  >
                    {form.etiket_adi || "Etiket seçin..."}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Etiket ara..." />
                    <CommandList>
                      <CommandEmpty>Etiket bulunamadı.</CommandEmpty>
                      <CommandGroup>
                        <CommandItem
                          value="KDV Ödemesi"
                          onSelect={() => {
                            setForm({
                              ...form,
                              etiket_id: 'kdv_odemesi_sys',
                              etiket_adi: 'KDV Ödemesi'
                            });
                            setEtiketOpen(false);
                          }}
                        >
                          <Check
                            className={cn(
                              "mr-2 h-4 w-4",
                              form.etiket_adi === "KDV Ödemesi" ? "opacity-100" : "opacity-0"
                            )}
                          />
                          <span className="font-semibold text-indigo-600">KDV Ödemesi</span>
                        </CommandItem>
                        {hesapKartlari.map((kart) => (
                          <CommandItem
                            key={kart.id}
                            value={kart.kayit_adi}
                            onSelect={() => {
                              setForm({
                                ...form,
                                etiket_id: kart.id,
                                etiket_adi: kart.kayit_adi
                              });
                              setEtiketOpen(false);
                            }}
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4",
                                form.etiket_id === kart.id ? "opacity-100" : "opacity-0"
                              )}
                            />
                            {kart.kayit_adi}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            {/* Fatura Durumu */}
            <div className="space-y-2">
              <Label htmlFor="fatura_durumu">Fatura Durumu</Label>
              <Select
                value={form.fatura_durumu}
                onValueChange={(value) => setForm({ ...form, fatura_durumu: value, fatura_miktari: ['fatura_alindi', 'eksik_fatura', 'fis'].includes(value) ? form.fatura_miktari : '' })}
              >
                <SelectTrigger id="fatura_durumu" data-testid="cikis-fatura-durumu">
                  <SelectValue placeholder="Fatura durumu seçin" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="fatura_alindi">Fatura Alındı</SelectItem>
                  <SelectItem value="borc_verildi">Borç Verildi</SelectItem>
                  <SelectItem value="fatura_alinacak">Fatura Alınacak</SelectItem>
                  <SelectItem value="borc_odendi">Borç Ödendi</SelectItem>
                  <SelectItem value="eksik_fatura">Eksik Fatura</SelectItem>
                  <SelectItem value="fis">Fiş</SelectItem>
                  <SelectItem value="faturasiz_nakit">Faturasız Nakit</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Anlaşılan Fatura Miktarı (Toplam Tutar) */}
            {['fatura_alindi', 'eksik_fatura', 'fatura_alinacak', 'fis'].includes(form.fatura_durumu) && (
              <div className="space-y-2">
                <Label htmlFor="anlaşılan_fatura_miktari">Anlaşılan Fatura Miktarı (₺) *</Label>
                <Input
                  id="anlaşılan_fatura_miktari"
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={form.anlaşılan_fatura_miktari}
                  onChange={(e) => {
                    const totalAmount = e.target.value;
                    const kdvRate = parseFloat(form.kdv_orani) || 20;
                    
                    if (!totalAmount) {
                      setForm({ 
                        ...form, 
                        anlaşılan_fatura_miktari: '',
                        fatura_miktari: '',
                        kdv_tutari: ''
                      });
                      return;
                    }
                    
                    const netAmount = (parseFloat(totalAmount) / (1 + (kdvRate / 100))).toFixed(2);
                    const kdvAmount = (parseFloat(totalAmount) - parseFloat(netAmount)).toFixed(2);
                    
                    setForm({ 
                      ...form, 
                      anlaşılan_fatura_miktari: totalAmount,
                      fatura_miktari: totalAmount, // KDV Dahil
                      kdv_tutari: kdvAmount
                    });
                  }}
                  data-testid="cikis-anlaşılan-fatura-miktari"
                />
                <p className="text-xs text-gray-500">Takip için anlaşılan toplam (KDV Dahil) fatura miktarını girin</p>
              </div>
            )}

            {/* Fatura Miktarı (Matrah) ve KDV */}
            {['fatura_alindi', 'eksik_fatura', 'fis', 'fatura_alinacak'].includes(form.fatura_durumu) && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="fatura_miktari">Fatura Miktarı (₺) (KDV Dahil)</Label>
                  <Input
                    id="fatura_miktari"
                    type="number"
                    step="0.01"
                    placeholder="Otomatik hesaplanır..."
                    value={form.fatura_miktari}
                    readOnly
                    className="bg-gray-50 text-gray-500 font-medium"
                    data-testid="cikis-fatura-miktari"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="kdv_orani">KDV Oranı (%)</Label>
                    <Input
                      id="kdv_orani"
                      type="number"
                      step="1"
                      placeholder="20"
                      value={form.kdv_orani}
                      onChange={(e) => {
                        const rate = e.target.value;
                        const kdvRateNum = parseFloat(rate) || 0;
                        
                        // Eğer toplam miktar girilmişse, net tutarı ona göre yeniden hesapla
                        if (form.anlaşılan_fatura_miktari) {
                          const totalAmount = parseFloat(form.anlaşılan_fatura_miktari);
                          const netAmount = (totalAmount / (1 + (kdvRateNum / 100))).toFixed(2);
                          const kdvAmount = (totalAmount - parseFloat(netAmount)).toFixed(2);
                          
                          setForm({ 
                            ...form, 
                            kdv_orani: rate,
                            fatura_miktari: netAmount,
                            kdv_tutari: kdvAmount
                          });
                        } else {
                          setForm({ ...form, kdv_orani: rate });
                        }
                      }}
                      data-testid="cikis-kdv-orani"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="kdv_tutari">KDV Tutarı (₺)</Label>
                    <Input
                      id="kdv_tutari"
                      type="number"
                      step="0.01"
                      placeholder="Otomatik hesaplanır..."
                      value={form.kdv_tutari}
                      readOnly
                      className="bg-gray-50 text-gray-500 font-medium"
                      data-testid="cikis-kdv-tutari"
                    />
                  </div>
                </div>
              </>
            )}

            {/* Eksik Fatura için Şu An Kesilen Kısmî Fatura Bilgileri */}
            {form.fatura_durumu === 'eksik_fatura' && (
              <div className="mt-6 p-4 border border-blue-200 bg-blue-50/50 rounded-lg space-y-4">
                <h4 className="font-semibold text-blue-800 flex items-center">
                  <FileText className="h-4 w-4 mr-2" /> Şu An Alınan Fatura Bilgileri
                </h4>
                
                <div className="space-y-2">
                  <Label htmlFor="suan_kesilen_miktar" className="text-blue-900">Şu An Alınan Miktar (₺) (KDV Dahil) *</Label>
                  <Input
                    id="suan_kesilen_miktar"
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={form.suan_kesilen_miktar || ''}
                    onChange={(e) => {
                      const totalAmount = e.target.value;
                      const kdvRate = parseFloat(form.suan_kesilen_kdv_orani) || 20;
                      
                      if (!totalAmount) {
                        setForm({ 
                          ...form, 
                          suan_kesilen_miktar: '',
                          suan_kesilen_fatura_miktari: '',
                          suan_kesilen_kdv_tutari: ''
                        });
                        return;
                      }
                      
                      const netAmount = (parseFloat(totalAmount) / (1 + (kdvRate / 100))).toFixed(2);
                      const kdvAmount = (parseFloat(totalAmount) - parseFloat(netAmount)).toFixed(2);
                      
                      setForm({ 
                        ...form, 
                        suan_kesilen_miktar: totalAmount,
                        suan_kesilen_fatura_miktari: totalAmount, // KDV Dahil
                        suan_kesilen_kdv_tutari: kdvAmount
                      });
                    }}
                    className="border-blue-300 focus-visible:ring-blue-500"
                  />
                  <p className="text-xs text-blue-700/70">Şu an aldığınız faturanın KDV dahil toplam tutarı (Kalan miktar takip edilecektir)</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="suan_kesilen_kdv_orani" className="text-blue-900">KDV Oranı (%)</Label>
                    <Input
                      id="suan_kesilen_kdv_orani"
                      type="number"
                      step="1"
                      placeholder="20"
                      value={form.suan_kesilen_kdv_orani || '20'}
                      onChange={(e) => {
                        const rate = e.target.value;
                        const kdvRateNum = parseFloat(rate) || 0;
                        
                        if (form.suan_kesilen_miktar) {
                          const totalAmount = parseFloat(form.suan_kesilen_miktar);
                          const netAmount = (totalAmount / (1 + (kdvRateNum / 100))).toFixed(2);
                          const kdvAmount = (totalAmount - parseFloat(netAmount)).toFixed(2);
                          
                          setForm({ 
                            ...form, 
                            suan_kesilen_kdv_orani: rate,
                            suan_kesilen_fatura_miktari: totalAmount, // KDV Dahil
                            suan_kesilen_kdv_tutari: kdvAmount
                          });
                        } else {
                          setForm({ ...form, suan_kesilen_kdv_orani: rate });
                        }
                      }}
                      className="border-blue-300 focus-visible:ring-blue-500"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="suan_kesilen_kdv_tutari" className="text-blue-900">KDV Tutarı (₺)</Label>
                    <Input
                      id="suan_kesilen_kdv_tutari"
                      type="number"
                      step="0.01"
                      value={form.suan_kesilen_kdv_tutari || ''}
                      readOnly
                      className="bg-blue-100/50 text-blue-800 font-medium border-blue-200"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="suan_kesilen_fatura_miktari" className="text-blue-900">Şu An Alınan Fatura Miktarı (₺) (KDV Dahil)</Label>
                  <Input
                    id="suan_kesilen_fatura_miktari"
                    type="number"
                    step="0.01"
                    value={form.suan_kesilen_fatura_miktari || ''}
                    readOnly
                    className="bg-blue-100/50 text-blue-800 font-medium border-blue-200"
                  />
                </div>
              </div>
            )}

            {/* Açıklama */}
            <div className="space-y-2">
              <Label htmlFor="aciklama">Açıklama</Label>
              <Textarea
                id="aciklama"
                placeholder="Açıklama yazın..."
                value={form.aciklama}
                onChange={(e) => setForm({ ...form, aciklama: e.target.value })}
                rows={3}
                data-testid="cikis-aciklama"
              />
            </div>

            {/* Belgeler - Çoklu Yükleme */}
            <div className="space-y-2">
              <Label>Belgeler</Label>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Input
                    type="file"
                    accept="image/*,.pdf"
                    onChange={handleFileUpload}
                    className="hidden"
                    id="cikis-belge-upload"
                    multiple
                    data-testid="cikis-belge-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => document.getElementById('cikis-belge-upload').click()}
                    className="gap-2"
                  >
                    <Upload className="h-4 w-4" />
                    Belge Ekle
                  </Button>
                  <span className="text-xs text-gray-500">Birden fazla dosya seçebilirsiniz</span>
                </div>
                {form.belgeler.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {form.belgeler.map((belge, index) => (
                      <div key={index} className="flex items-center gap-2 px-3 py-1 bg-gray-100 rounded-lg">
                        <FileText className="h-4 w-4 text-blue-600" />
                        <span className="text-sm max-w-[150px] truncate">{belge.name}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0"
                          onClick={() => removeBelge(index)}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Borç Takip Oluştur */}
            {!editingCikis && (
              <div className="space-y-4 pt-4 border-t mt-4">
                <div className="flex items-center justify-between bg-indigo-50 p-4 rounded-lg border border-indigo-100">
                  <div className="space-y-0.5">
                    <Label className="text-base font-semibold text-indigo-900">Borç Takibi Oluştur</Label>
                    <p className="text-sm text-indigo-700">Bu işlemle birlikte detaylı ödeme ve fatura takvimi oluştur.</p>
                  </div>
                  <Switch
                    checked={form.create_takip}
                    onCheckedChange={(c) => setForm({ ...form, create_takip: c })}
                  />
                </div>

                {form.create_takip && (
                  <div className="space-y-6 border border-indigo-100 rounded-xl p-5 bg-indigo-50/30">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Toplam Borç Tutarı (₺)</Label>
                        <Input
                          type="number"
                          value={form.takip_toplam_tutar}
                          onChange={(e) => setForm({...form, takip_toplam_tutar: e.target.value})}
                          placeholder="Örn: 50000"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Anlaşılan Toplam Fatura Tutarı (KDV Dahil) (₺)</Label>
                        <Input
                          type="number"
                          value={form.takip_fatura_miktari}
                          onChange={(e) => setForm({...form, takip_fatura_miktari: e.target.value})}
                          placeholder="Örn: 50000"
                        />
                      </div>
                    </div>

                    {/* KDV Detayları */}
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <Label className="text-sm font-semibold">Farklı KDV Dilimleri</Label>
                        <Button 
                          type="button" 
                          variant="outline" 
                          size="sm" 
                          onClick={() => setForm({
                            ...form, 
                            takip_kdv_detaylari: [...form.takip_kdv_detaylari, { id: Date.now(), miktar: '', kdv_orani: '20', kdv_tutari: '' }]
                          })}
                        >
                          <Plus className="h-4 w-4 mr-1" /> KDV Ekle
                        </Button>
                      </div>
                      {form.takip_kdv_detaylari.map((kdv, index) => (
                        <div key={kdv.id} className="flex items-end gap-3 bg-white p-3 rounded border">
                          <div className="flex-1 space-y-1">
                            <Label className="text-xs">Matrah / Tutar</Label>
                            <Input 
                              type="number" 
                              value={kdv.miktar} 
                              onChange={(e) => {
                                const newArr = [...form.takip_kdv_detaylari];
                                newArr[index].miktar = e.target.value;
                                if (e.target.value && newArr[index].kdv_orani) {
                                  newArr[index].kdv_tutari = (parseFloat(e.target.value) * (parseFloat(newArr[index].kdv_orani) / 100)).toFixed(2);
                                }
                                setForm({...form, takip_kdv_detaylari: newArr});
                              }}
                            />
                          </div>
                          <div className="w-24 space-y-1">
                            <Label className="text-xs">KDV (%)</Label>
                            <Input 
                              type="number" 
                              value={kdv.kdv_orani} 
                              onChange={(e) => {
                                const newArr = [...form.takip_kdv_detaylari];
                                newArr[index].kdv_orani = e.target.value;
                                if (newArr[index].miktar && e.target.value) {
                                  newArr[index].kdv_tutari = (parseFloat(newArr[index].miktar) * (parseFloat(e.target.value) / 100)).toFixed(2);
                                }
                                setForm({...form, takip_kdv_detaylari: newArr});
                              }}
                            />
                          </div>
                          <div className="flex-1 space-y-1">
                            <Label className="text-xs">KDV Tutarı</Label>
                            <Input 
                              type="number" 
                              value={kdv.kdv_tutari} 
                              onChange={(e) => {
                                const newArr = [...form.takip_kdv_detaylari];
                                newArr[index].kdv_tutari = e.target.value;
                                setForm({...form, takip_kdv_detaylari: newArr});
                              }}
                            />
                          </div>
                          <Button 
                            type="button" 
                            variant="ghost" 
                            className="text-red-500 mb-0.5 px-2"
                            onClick={() => {
                              const newArr = form.takip_kdv_detaylari.filter((_, i) => i !== index);
                              setForm({...form, takip_kdv_detaylari: newArr});
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>

                    {/* Ödeme Tablosu */}
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <Label className="text-sm font-semibold">Ödeme Tablosu (Taksitler)</Label>
                        <Button 
                          type="button" 
                          variant="outline" 
                          size="sm" 
                          onClick={() => setForm({
                            ...form, 
                            takip_odemeler: [...form.takip_odemeler, { id: Date.now(), tarih: '', tutar: '', odeme_sekli: 'havale_eft', aciklama: '' }]
                          })}
                        >
                          <Plus className="h-4 w-4 mr-1" /> Ödeme Ekle
                        </Button>
                      </div>
                      {form.takip_odemeler.map((odeme, index) => (
                        <div key={odeme.id} className="grid grid-cols-12 gap-2 bg-white p-3 rounded border items-end">
                          <div className="col-span-3 space-y-1">
                            <Label className="text-xs">Tarih</Label>
                            <Input 
                              type="date" 
                              value={odeme.tarih} 
                              onChange={(e) => {
                                const newArr = [...form.takip_odemeler];
                                newArr[index].tarih = e.target.value;
                                setForm({...form, takip_odemeler: newArr});
                              }}
                            />
                          </div>
                          <div className="col-span-3 space-y-1">
                            <Label className="text-xs">Tutar</Label>
                            <Input 
                              type="number" 
                              value={odeme.tutar} 
                              onChange={(e) => {
                                const newArr = [...form.takip_odemeler];
                                newArr[index].tutar = e.target.value;
                                setForm({...form, takip_odemeler: newArr});
                              }}
                            />
                          </div>
                          <div className="col-span-5 space-y-1">
                            <Label className="text-xs">Açıklama</Label>
                            <Input 
                              value={odeme.aciklama} 
                              onChange={(e) => {
                                const newArr = [...form.takip_odemeler];
                                newArr[index].aciklama = e.target.value;
                                setForm({...form, takip_odemeler: newArr});
                              }}
                            />
                          </div>
                          <div className="col-span-1 flex justify-center">
                            <Button 
                              type="button" 
                              variant="ghost" 
                              className="text-red-500 px-2"
                              onClick={() => {
                                const newArr = form.takip_odemeler.filter((_, i) => i !== index);
                                setForm({...form, takip_odemeler: newArr});
                              }}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>

                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>
              İptal
            </Button>
            <Button
              onClick={handleSubmit}
              className="bg-orange-600 hover:bg-orange-700"
              data-testid="cikis-submit-btn"
            >
              {editingCikis ? 'Güncelle' : 'Kaydet'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Document Preview Modal */}
      <DocumentPreviewModal
        open={showDocPreview}
        onOpenChange={setShowDocPreview}
        belgeler={previewBelgeler}
        title="Çıkış Belgeleri"
      />

      {/* Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5 text-red-600" />
              Çıkış Detayı
              {detailRecord?.seri_no && (
                <Badge className="bg-red-100 text-red-700 ml-2">{detailRecord.seri_no}</Badge>
              )}
            </DialogTitle>
          </DialogHeader>
          {detailRecord && (
            <div className="space-y-4 py-4">
              {/* Seri No ve Tarih/Saat */}
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-1">
                  <Label className="text-gray-500 text-sm">Seri No</Label>
                  <p className="font-mono font-medium text-red-700">{detailRecord.seri_no || '-'}</p>
                </div>
                <div className="space-y-1">
                  <Label className="text-gray-500 text-sm">Tarih</Label>
                  <p className="font-medium">{detailRecord.tarih || '-'}</p>
                </div>
                <div className="space-y-1">
                  <Label className="text-gray-500 text-sm">Saat</Label>
                  <p className="font-medium">{detailRecord.saat || '-'}</p>
                </div>
              </div>

              <div className="space-y-1">
                <Label className="text-gray-500 text-sm">Cari Hesap</Label>
                <p className="font-medium">{detailRecord.cari_hesap_adi || '-'}</p>
              </div>

              {/* Miktar ve Para Birimi */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-gray-500 text-sm">Miktar</Label>
                  <p className="font-semibold text-red-600 text-lg">{formatCurrency(detailRecord.miktar, detailRecord.para_birimi || 'TL')}</p>
                </div>
                <div className="space-y-1">
                  <Label className="text-gray-500 text-sm">Para Birimi</Label>
                  <p className="font-medium">{detailRecord.para_birimi || 'TL'}</p>
                </div>
              </div>

              {/* Anlaşılan Fatura Miktarı */}
              {detailRecord.anlaşılan_fatura_miktari && (
                <div className="space-y-1 p-3 bg-purple-50 rounded-lg border border-purple-200">
                  <Label className="text-purple-600 text-sm font-medium">Anlaşılan Fatura Miktarı</Label>
                  <p className="font-semibold text-purple-700 text-lg">
                    ₺{parseFloat(detailRecord.anlaşılan_fatura_miktari).toLocaleString('tr-TR', {minimumFractionDigits: 2})}
                  </p>
                </div>
              )}

              <div className="space-y-1">
                <Label className="text-gray-500 text-sm">Hesap (Para Çekilen)</Label>
                <p className="font-medium">
                  {detailRecord.hesap_tipi === 'kredi_karti' ? '💳' : detailRecord.hesap_tipi === 'nakit_kasa' ? '💵' : '🏦'} {detailRecord.hesap_adi || '-'}
                </p>
              </div>

              {/* Kredi Kartı Taksit Bilgisi */}
              {detailRecord.hesap_tipi === 'kredi_karti' && detailRecord.taksit_sayisi > 1 && (
                <div className="grid grid-cols-3 gap-4 p-2 bg-indigo-50 rounded">
                  <div className="space-y-1">
                    <Label className="text-gray-500 text-xs">Taksit</Label>
                    <p className="font-medium text-indigo-700">{detailRecord.taksit_no}/{detailRecord.toplam_taksit}</p>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-gray-500 text-xs">Toplam Tutar</Label>
                    <p className="font-medium">{formatCurrency(detailRecord.ana_tutar)}</p>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-gray-500 text-xs">Taksit Tutarı</Label>
                    <p className="font-medium">{formatCurrency(detailRecord.miktar)}</p>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-gray-500 text-sm">Çek Karşılığı</Label>
                  <p className="font-medium">
                    {detailRecord.cek_karsiligi ? (
                      <Badge className="bg-indigo-100 text-indigo-700">Evet - Çek No: {detailRecord.cek_no || '-'}</Badge>
                    ) : (
                      <Badge variant="outline">Hayır</Badge>
                    )}
                  </p>
                </div>
                <div className="space-y-1">
                  <Label className="text-gray-500 text-sm">Fortis Şube</Label>
                  <p className="font-medium">{detailRecord.fortis_sube || '-'}</p>
                </div>
              </div>

              <div className="space-y-1">
                <Label className="text-gray-500 text-sm">Etiket (Hesap Kartı)</Label>
                <p className="font-medium">{detailRecord.etiket_adi || '-'}</p>
              </div>

              {/* Takipteki Borç Bilgisi */}
              {detailRecord.takipteki_borc && (
                <div className="space-y-2 p-3 bg-orange-50 rounded-lg border border-orange-200">
                  <Label className="text-orange-700 font-medium flex items-center gap-2">
                    <ArrowUpCircle className="h-4 w-4" />
                    Takipteki Borç Bilgisi
                  </Label>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    {detailRecord.borc_odeme_text && (
                      <div>
                        <span className="text-gray-500">Ödeme:</span>
                        <span className="ml-2 font-medium">{detailRecord.borc_odeme_text}</span>
                      </div>
                    )}
                    {detailRecord.borc_takip_id && (
                      <div>
                        <span className="text-gray-500">Takip ID:</span>
                        <span className="ml-2 font-mono text-xs">{detailRecord.borc_takip_id}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* İş Kaydı Bilgisi */}
              {detailRecord.job_id && (
                <div className="space-y-2 p-3 bg-indigo-50 rounded-lg border border-indigo-200">
                  <Label className="text-indigo-700 font-medium flex items-center gap-2">
                    <Briefcase className="h-4 w-4" />
                    Bağlı İş Kaydı
                  </Label>
                  <div className="flex items-center justify-between">
                    <p className="font-medium text-indigo-800">{detailRecord.job_konu || 'İş Kaydı'}</p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setSelectedJobId(detailRecord.job_id);
                        setShowJobPopup(true);
                      }}
                      className="text-indigo-600 border-indigo-300 hover:bg-indigo-100"
                    >
                      <Eye className="h-3 w-3 mr-1" />
                      Görüntüle
                    </Button>
                  </div>
                </div>
              )}

              {/* Fatura Durumu - Düzenlenebilir */}
              <div className="space-y-2">
                <Label className="text-gray-500 text-sm">Fatura Durumu</Label>
                <Select
                  value={detailRecord.fatura_durumu || ''}
                  onValueChange={handleFaturaDurumuChange}
                >
                  <SelectTrigger className="w-full" data-testid="detail-cikis-fatura-durumu-select">
                    <SelectValue placeholder="Fatura durumu seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fatura_alindi">Fatura Alındı</SelectItem>
                    <SelectItem value="borc_verildi">Borç Verildi</SelectItem>
                    <SelectItem value="fatura_alinacak">Fatura Alınacak</SelectItem>
                    <SelectItem value="borc_odendi">Borç Ödendi</SelectItem>
                    <SelectItem value="eksik_fatura">Eksik Fatura</SelectItem>
                    <SelectItem value="fis">Fiş</SelectItem>
                    <SelectItem value="faturasiz_nakit">Faturasız Nakit</SelectItem>
                  </SelectContent>
                </Select>
                {['fatura_alindi', 'eksik_fatura', 'fis'].includes(detailRecord.fatura_durumu) && detailRecord.fatura_miktari && (
                  <span className="font-semibold text-green-600 text-sm">Fatura Matrahı: {formatCurrency(detailRecord.fatura_miktari)}</span>
                )}
              </div>

              {/* Alınan Faturalar - Çoklu Fatura Desteği */}
              <div className="space-y-3 p-4 bg-orange-50 rounded-lg border border-orange-200">
                <div className="flex items-center justify-between">
                  <Label className="text-orange-700 font-semibold">Alınan Faturalar</Label>
                  {(detailRecord.alinan_faturalar && detailRecord.alinan_faturalar.length > 0) ? (
                    <Badge className="bg-orange-100 text-orange-700">
                      Toplam: {formatCurrency(detailRecord.alinan_fatura_toplam || detailRecord.alinan_faturalar.reduce((sum, f) => sum + (f.miktar || 0), 0), detailRecord.para_birimi)}
                    </Badge>
                  ) : ['fatura_alindi', 'eksik_fatura', 'fis'].includes(detailRecord.fatura_durumu) && detailRecord.fatura_miktari ? (
                    <Badge className="bg-orange-100 text-orange-700">
                      Toplam: {formatCurrency(detailRecord.fatura_miktari, detailRecord.para_birimi)}
                    </Badge>
                  ) : null}
                </div>
                
                {/* Mevcut Faturalar Listesi */}
                {detailRecord.alinan_faturalar && detailRecord.alinan_faturalar.length > 0 ? (
                  <div className="space-y-2">
                    {detailRecord.alinan_faturalar.map((fatura, idx) => (
                      <div key={fatura.id || idx} className="flex items-center justify-between bg-white p-2 rounded border">
                        <div className="flex items-center gap-3">
                          <span className="text-orange-600 font-semibold">{formatCurrency(fatura.miktar, detailRecord.para_birimi)}</span>
                          <span className="text-gray-500 text-sm">{fatura.tarih}</span>
                          {fatura.belge && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 px-2 text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                              onClick={() => window.open(fatura.belge.url, '_blank')}
                            >
                              <Eye className="h-3 w-3 mr-1" />
                              Belge Görüntüle
                            </Button>
                          )}
                        </div>
                        <Badge className="bg-orange-100 text-orange-700 text-xs">#{idx + 1}</Badge>
                      </div>
                    ))}
                  </div>
                ) : ['fatura_alindi', 'eksik_fatura', 'fis'].includes(detailRecord.fatura_durumu) && detailRecord.fatura_miktari ? (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between bg-white p-2 rounded border">
                      <div className="flex items-center gap-3">
                        <span className="text-orange-600 font-semibold">{formatCurrency(detailRecord.fatura_miktari, detailRecord.para_birimi)}</span>
                        <span className="text-gray-500 text-sm">{detailRecord.tarih}</span>
                        {detailRecord.belgeler && detailRecord.belgeler.length > 0 && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 px-2 text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                            onClick={() => window.open(detailRecord.belgeler[0].url, '_blank')}
                          >
                            <Eye className="h-3 w-3 mr-1" />
                            Belge Görüntüle
                          </Button>
                        )}
                      </div>
                      <Badge className="bg-orange-100 text-orange-700 text-xs">#1</Badge>
                    </div>
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">Henüz fatura eklenmemiş</p>
                )}
                
                {/* Yeni Fatura Ekle Butonu - Her zaman göster */}
                <Button 
                  onClick={openFaturaModal}
                  className="bg-orange-600 hover:bg-orange-700 w-full"
                  data-testid="add-alinan-fatura-btn"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  {(detailRecord.alinan_faturalar && detailRecord.alinan_faturalar.length > 0) || (['fatura_alindi', 'eksik_fatura', 'fis'].includes(detailRecord.fatura_durumu) && detailRecord.fatura_miktari) ? 'Yeni Fatura Ekle' : 'Alınan Fatura Ekle'}
                </Button>
              </div>

              <div className="space-y-1">
                <Label className="text-gray-500 text-sm">Açıklama</Label>
                <p className="font-medium bg-gray-50 p-3 rounded-lg">{detailRecord.aciklama || 'Açıklama yok'}</p>
              </div>

              {detailRecord.belgeler && detailRecord.belgeler.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-gray-500 text-sm">Belgeler ({detailRecord.belgeler.length})</Label>
                  <div className="flex flex-wrap gap-2">
                    {detailRecord.belgeler.map((belge, idx) => (
                      <Badge key={idx} variant="outline" className="flex items-center gap-1">
                        <FileText className="h-3 w-3" />
                        {belge.name || `Belge ${idx + 1}`}
                      </Badge>
                    ))}
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      setPreviewBelgeler(detailRecord.belgeler);
                      setShowDocPreview(true);
                    }}
                  >
                    Belgeleri Görüntüle
                  </Button>
                </div>
              )}

              <div className="pt-4 border-t text-xs text-gray-500">
                <p>Oluşturulma: {detailRecord.created_at ? new Date(detailRecord.created_at).toLocaleString('tr-TR') : '-'}</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setShowDetailModal(false)}>Kapat</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Alınan Fatura Ekleme Modal */}
      <Dialog open={showFaturaModal} onOpenChange={setShowFaturaModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-orange-700">Alınan Fatura Ekle</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="fatura_miktari">Fatura Miktarı (₺) *</Label>
              <Input
                id="fatura_miktari"
                type="number"
                step="0.01"
                placeholder="0.00"
                value={faturaForm.fatura_miktari}
                onChange={(e) => setFaturaForm({ ...faturaForm, fatura_miktari: e.target.value })}
                data-testid="alinan-fatura-miktar"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="kdv_tutari">KDV Tutarı (₺)</Label>
              <Input
                id="kdv_tutari"
                type="number"
                step="0.01"
                placeholder="0.00"
                value={faturaForm.kdv_tutari}
                onChange={(e) => setFaturaForm({ ...faturaForm, kdv_tutari: e.target.value })}
                data-testid="alinan-fatura-kdv"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="fatura_tarihi">Fatura Tarihi</Label>
              <Input
                id="fatura_tarihi"
                type="date"
                value={faturaForm.fatura_tarihi}
                onChange={(e) => setFaturaForm({ ...faturaForm, fatura_tarihi: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="fatura_belgesi">Fatura Belgesi *</Label>
              <Input
                id="fatura_belgesi"
                type="file"
                accept="image/*,.pdf"
                onChange={handleFaturaFileUpload}
              />
              {faturaForm.fatura_belgesi && (
                <div className="flex items-center gap-2 text-orange-600">
                  <FileText className="h-4 w-4" />
                  <span className="text-sm">{faturaForm.fatura_belgesi.name}</span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-6 px-2 text-orange-600 hover:text-orange-700"
                    onClick={() => window.open(faturaForm.fatura_belgesi.url, '_blank')}
                  >
                    <Eye className="h-3 w-3 mr-1" />
                    Görüntüle
                  </Button>
                </div>
              )}
              {!faturaForm.fatura_belgesi && (
                <p className="text-red-500 text-xs">Fatura belgesi yüklemek zorunludur</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFaturaModal(false)}>İptal</Button>
            <Button 
              onClick={handleFaturaSubmit} 
              disabled={faturaUploading}
              className="bg-orange-600 hover:bg-orange-700"
            >
              {faturaUploading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Fatura Ekle
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Job Detail Popup */}
      <JobDetailPopup
        jobId={selectedJobId}
        isOpen={showJobPopup}
        onClose={() => {
          setShowJobPopup(false);
          setSelectedJobId(null);
        }}
      />

      {/* Board View - Approval Modal */}
      {isBoardView && (
        <Dialog open={showApprovalModal} onOpenChange={setShowApprovalModal}>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileCheck className="h-5 w-5 text-indigo-600" />
                İnceleme / Onay
              </DialogTitle>
            </DialogHeader>
            
            {approvalRecord && (
              <div className="space-y-4 py-4">
                {/* Record Info */}
                <div className="bg-gray-50 p-3 rounded-lg text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Seri No:</span>
                    <span className="font-mono">{approvalRecord.seri_no}</span>
                  </div>
                  <div className="flex justify-between mt-1">
                    <span className="text-gray-500">Miktar:</span>
                    <span className="font-semibold text-red-600">{formatCurrency(approvalRecord.miktar, approvalRecord.para_birimi || 'TL')}</span>
                  </div>
                  <div className="flex justify-between mt-1">
                    <span className="text-gray-500">Cari Hesap:</span>
                    <span>{approvalRecord.cari_hesap_adi || '-'}</span>
                  </div>
                </div>

                {/* Reviewer Name */}
                <div className="space-y-2">
                  <Label htmlFor="approval_by">İnceleyen Adı *</Label>
                  <Input
                    id="approval_by"
                    value={approvalForm.approved_by}
                    onChange={(e) => setApprovalForm(prev => ({ ...prev, approved_by: e.target.value }))}
                    placeholder="Adınızı girin"
                  />
                </div>

                {/* Review Result Buttons */}
                <div className="space-y-2">
                  <Label>İnceleme Sonucu *</Label>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant={approvalForm.status === 'dogru' ? 'default' : 'outline'}
                      className={`flex-1 ${approvalForm.status === 'dogru' ? 'bg-green-600 hover:bg-green-700' : 'hover:bg-green-50 hover:border-green-300'}`}
                      onClick={() => setApprovalForm(prev => ({ ...prev, status: 'dogru' }))}
                    >
                      <Check className="h-4 w-4 mr-2" />
                      Doğru
                    </Button>
                    <Button
                      type="button"
                      variant={approvalForm.status === 'supheli' ? 'default' : 'outline'}
                      className={`flex-1 ${approvalForm.status === 'supheli' ? 'bg-amber-500 hover:bg-amber-600' : 'hover:bg-amber-50 hover:border-amber-300'}`}
                      onClick={() => setApprovalForm(prev => ({ ...prev, status: 'supheli' }))}
                    >
                      <div className="h-4 w-4 mr-2 rounded-full border-2 border-current" />
                      Şüpheli
                    </Button>
                    <Button
                      type="button"
                      variant={approvalForm.status === 'yanlis' ? 'default' : 'outline'}
                      className={`flex-1 ${approvalForm.status === 'yanlis' ? 'bg-red-600 hover:bg-red-700' : 'hover:bg-red-50 hover:border-red-300'}`}
                      onClick={() => setApprovalForm(prev => ({ ...prev, status: 'yanlis' }))}
                    >
                      <X className="h-4 w-4 mr-2" />
                      Yanlış
                    </Button>
                  </div>
                </div>

                {/* Description */}
                <div className="space-y-2">
                  <Label htmlFor="approval_note">Açıklama</Label>
                  <Textarea
                    id="approval_note"
                    value={approvalForm.note}
                    onChange={(e) => setApprovalForm(prev => ({ ...prev, note: e.target.value }))}
                    placeholder="İnceleme notlarınızı yazın..."
                    rows={3}
                  />
                </div>

                {/* Document Upload */}
                <div className="space-y-2">
                  <Label>Belge Ekle</Label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-3 text-center hover:border-indigo-400 transition-colors">
                    <input
                      type="file"
                      multiple
                      onChange={handleApprovalFileUpload}
                      className="hidden"
                      id="approval-file-upload-expenses"
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                    />
                    <label htmlFor="approval-file-upload-expenses" className="cursor-pointer">
                      <Upload className="h-6 w-6 mx-auto text-gray-400 mb-1" />
                      <p className="text-sm text-gray-600">Dosya seçmek için tıklayın</p>
                    </label>
                  </div>
                  
                  {/* Uploaded Files List */}
                  {approvalForm.belgeler.length > 0 && (
                    <div className="space-y-1 mt-2">
                      {approvalForm.belgeler.map((doc, index) => (
                        <div key={index} className="flex items-center justify-between bg-indigo-50 p-2 rounded text-sm">
                          <div className="flex items-center gap-2">
                            <FileText className="h-4 w-4 text-indigo-600" />
                            <span className="truncate max-w-[250px]">{doc.name}</span>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setApprovalForm(prev => ({ 
                              ...prev, 
                              belgeler: prev.belgeler.filter((_, i) => i !== index) 
                            }))}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50 h-6 px-2"
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowApprovalModal(false)}>İptal</Button>
              <Button 
                onClick={handleApprovalSubmit}
                disabled={approvalSubmitting}
                className="bg-indigo-600 hover:bg-indigo-700"
              >
                {approvalSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Kaydet
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default AccountingExpenses;
